import {
  STEWARD_ROLE_IDS,
  STEWARD_ADVISOR_FIRM_ROLE_IDS,
  SHARING_PILLARS,
  PILLAR_ROLE_IDS,
  // steward-app#1260 — the `people` pillar's second role id, labelled explicitly in
  // `ROLE_LABELS` because `additionalRoleIds` carries no label to map over.
  PEOPLE_TAX_ID_REVEAL_ROLE_ID,
  // steward-app#1950 — the documents pillar's OFF role, labelled explicitly for the same reason.
  DOCUMENTS_SHARED_FOLDER_ROLE_ID,
  // steward-app#2581 — the four cash flow roles, labelled explicitly (they are not pillars).
  CASH_FLOW_ROLE_IDS,
  CASH_FLOW_FIRM_ROLE_IDS,
  PLATFORM_READ_ONLY_ROLE_IDS,
  PLATFORM_ADMIN_ROLE_IDS,
  // steward-app#835 — labels for the two ids `familyRoleIds` now seeds onto every family-only
  // folder's access list. See the ⚠️ in `ROLE_LABELS`.
  PLATFORM_WRITER_ROLE_IDS,
  isReadOnlyRoleSet,
  isAdminRoleSet,
  type SharingPillarId,
  type FirmCashFlowMode,
  type CredentialCategory,
} from '@steward-app/steward-types';
import { sharingAreasEnabled } from '../lib/appEnv';

export interface Resource<A> {
  id: string;
  attributes: A;
  status: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

// ── AI assistant chat threads (docs/13-ai-assistant.md) ──
export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  ts: string;
}

/** A thread summary as returned by GET /assistant/threads (no transcript). */
export interface ChatThreadSummary {
  id: string;
  title: string;
  lastMessageAt: string;
  messageCount: number;
}

/** A thread's full transcript from GET /assistant/threads/{id}. */
export interface ChatThread {
  id: string;
  title: string;
  messages: ChatMessage[];
  lastMessageAt: string;
}

// ── Product feedback (docs/23-feedback-capability.md) ──
export type FeedbackCategory = 'idea' | 'question' | 'issue' | 'compliment' | 'other';

/** Category display labels + canonical option order — shared by the composer
 *  (FeedbackForm's category chips) and the "Your feedback" list (Feedback.tsx). */
export const FEEDBACK_CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: 'idea',       label: 'Idea' },
  { value: 'question',   label: 'Question' },
  { value: 'issue',      label: 'Problem' },
  { value: 'compliment', label: 'Compliment' },
  { value: 'other',      label: 'Other' },
];

export const FEEDBACK_CATEGORY_LABEL: Record<FeedbackCategory, string> =
  Object.fromEntries(FEEDBACK_CATEGORIES.map(c => [c.value, c.label])) as Record<FeedbackCategory, string>;

/** Request body for POST /feedback. `category`/`context` are optional. */
export interface FeedbackInput {
  message: string;
  category?: FeedbackCategory;
  /** Where it was sent from — 'assistant' | 'feedback-page' | … */
  source?: string;
  /** Freeform origin hint (e.g. the active chat thread id). */
  context?: string;
}

/**
 * The feedback lifecycle (docs/23-feedback-capability.md §3.2). Only a platform
 * operator ever moves this; a member only ever reads it. `archived` never reaches
 * the SPA (filtered server-side).
 */
export type FeedbackStatus =
  | 'submitted' | 'reviewed' | 'planned' | 'in_progress' | 'resolved' | 'declined' | 'archived';

/**
 * Display label for each {@link FeedbackStatus} (§3.2, §8.2). No dashes — house copy
 * rule.
 *
 * A Map, not an object literal — load-bearing, not stylistic (crossmarc/steward-app#547,
 * caught on security review of #1095: the issue's own sweep missed this site).
 * `FeedbackStatus` is a compile-time-only claim about the value: appcore's feedback CTYPE
 * stores `status` as free text (only `category` is `z.enum`-guarded on create), and the
 * SPA never re-validates the shape of what the façade returns. `Feedback.tsx` renders
 * `{FEEDBACK_STATUS_LABEL[status]}` as a bare JSX CHILD (not a class string, unlike its
 * neighbour `STATUS_CHIP_CLASS`), so an object-literal lookup indexed by `constructor`
 * resolved through Object.prototype to a FUNCTION handed straight to React as a child —
 * "Functions are not valid as a React child" is a harder crash than a garbled badge.
 *
 * Use {@link feedbackStatusLabel} rather than indexing this directly.
 */
const FEEDBACK_STATUS_LABEL: ReadonlyMap<string, string> = new Map([
  ['submitted', 'Received'],
  ['reviewed', 'Reviewed'],
  ['planned', 'Planned'],
  ['in_progress', 'In progress'],
  ['resolved', 'Resolved'],
  ['declined', 'Not planned'],
  ['archived', 'Archived'], // never rendered — server-filtered out of the member list
]);

/**
 * Display label for a feedback item's `status`.
 *
 * Unlike `sourceLabel.ts`'s `code` fallback, an UNMAPPED status here is rendered
 * exactly as stored rather than a neutral placeholder word: this field moves only at
 * an operator's hand (never a family's), so an unrecognized value is far more likely
 * to be a new lifecycle stage added ops-side ahead of an SPA release than a hostile
 * or garbage input — and showing the operator's own word is more useful for triage
 * than hiding it behind "Unknown". React escapes it as a plain text child, so this is
 * safe even for a literal `constructor` — it just prints the word "constructor".
 */
export function feedbackStatusLabel(status: string): string {
  return FEEDBACK_STATUS_LABEL.get(status) ?? status;
}

/** One entry in a feedback thread. `by` is null for an operator's entry (their
 *  userId is withheld — render "Steward team" instead) and the author's own
 *  userId for the author's entries. */
export interface FeedbackResponse {
  id: string;
  body: string;
  authorType: 'operator' | 'author';
  by: string | null;
  at: string;
}

/**
 * A feedback item as the façade returns it (bare object, not wrapped in
 * `Resource<A>` — feedback carries its own id/version/status). `editable` is true
 * only while `status === 'submitted'` and no response has landed yet (§4.1) — the
 * author-edit window.
 */
export interface FeedbackItem {
  id: string;
  message: string;
  category: FeedbackCategory;
  source: string;
  context?: string;
  status: FeedbackStatus;
  submittedAt: string;
  updatedAt: string;
  version: number;
  editable: boolean;
  responses: FeedbackResponse[];
}

export interface Member {
  userId: string;
  roleIds: string[];
  status: 'active' | 'inactive';
  /** Display identity (FOP3); absent for profile-less/seeded members → fall back to the id. */
  email?: string;
  name?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  /** True when this person joined by ACCEPTING an invitation (crossmarc/appcore#645). The
   *  firm roster only takes people for whom this is true, so "Your firm" offers only them.
   *  Optional because an older façade does not send it; absent reads as not accepted. */
  inviteAccepted?: boolean;
  /**
   * Is this person one of a sponsoring firm's advisors, right now (steward-app#1507, SCS 3.4b)?
   * Mirrors the façade's `Member.sponsored` — see its own doc there for how it is derived.
   *
   * ⚠️ **A LABEL, never the gate.** `Access.tsx` hides Remove for a row where this is `true`,
   * but the façade refuses the DELETE outright regardless (`services/sponsoredFloor.ts`'s
   * `refuseMemberRemoval`), including against a stale bundle whose row does not carry this field
   * at all. Optional and absent-reads-as-`false` for the same deploy-skew reason as every other
   * field on this shape: an older façade sends nothing, which is the correct answer for a
   * workspace where sponsorship cannot exist yet.
   */
  sponsored?: boolean;
  /**
   * The id of the person record linked to this member (steward-app#1851), when exactly one
   * is. Sent only on `GET /members`, which appcore answers only for a `mem:manage` holder,
   * and absent when the façade could not read people. Settings, Members joins on this and
   * never on a person record's own `linkedUserId`. A label; nothing may authorize on it.
   */
  personId?: string;
}

/** A pending workspace invitation listed under Settings → Members (FOP1/FOP2). */
export interface Invite {
  token: string;
  email: string;
  roleIds: string[];
  invitedBy: string;
  status: 'pending' | 'accepted' | 'revoked';
  expiresAt: string;
  createdAt: string;
  /** F30 residual — set ONLY on the POST /invites (create/resend) response body, and
   *  only when a send was actually attempted: true = delivered, false = attempted and
   *  failed. Absent on GET /invites (the stored invite has no per-send delivery state)
   *  and absent when the mailer isn't configured server-side (no send attempted, e.g.
   *  local/dev) — never render a bare `false` as a hard error, and never treat absence
   *  as failure. See people/Access.tsx for how the SPA surfaces this. */
  emailDelivered?: boolean;
  /** F32 — ISO time the next send to this address is allowed. Present on the POST
   *  /invites 201 (the cooldown that send just started) and on GET /invites rows still
   *  inside a cooldown; ABSENT means a resend is available now. Unlike
   *  {@link emailDelivered} this one is server-stored, so the countdown it drives
   *  survives a reload and a second tab. */
  resendAvailableAt?: string;
}

/** One workspace the current user belongs to (from GET /me) — drives the switcher. */
export interface WorkspaceMembership {
  workspaceId: string;
  workspaceName: string;
  tenantId: string;
  roleIds: string[];
  /** The membership's own status (active/inactive/…). Stays `active` even when the
   *  workspace is suspended for billing — use {@link workspaceStatus} for that. */
  status: string;
  /** Denormalized workspace status (active | suspended | provisioning | …). Absent
   *  ⇒ never changed ⇒ active. A `suspended` workspace is one whose subscription is
   *  paused / trial-ended; it 403s on every scoped call (O4 suspended-state). */
  workspaceStatus?: string;
  /** The workspace's step-up assurance floor (appcore docs/28 §2). `'stepup'` means
   *  every workspace-scoped call refuses with `403 STEP_UP_REQUIRED`
   *  (`reason: 'floor'`) until this session verifies; absent ⇒ no floor.
   *
   *  Read ONLY by {@link AssuranceFloorGate}, and only to decide whether to hold
   *  first paint. It is deliberately NOT an authorization signal: it says the
   *  workspace is floored, never that this session fails to satisfy it. The 403
   *  still decides that, exactly as before. */
  assuranceFloor?: 'stepup';
  /** App Packs mounted on this workspace (appcore docs/26 Phase 0/1). Absent or
   *  empty ⇒ an ordinary `steward-app`-only (family) workspace. Consume only via
   *  {@link useWorkspaceKind} in `src/workspace/` — never pattern-match pack ids
   *  at a call site (crossmarc/appcore#246 will replace that function's body). */
  packs?: string[];
  /**
   * **Present, and `false`, ONLY when the façade's own `GET /workspace` read for this
   * membership failed** (`services/steward-app-api/src/handlers/me.ts`, steward-app#736 F1,
   * 2026-09-23). Absent (the ordinary case) or `true` means {@link packs} is a real read of
   * the workspace's mounted App Packs; `false` means `packs` degraded to `[]` because the
   * read failed, and that `[]` must NOT be read as "genuinely a family workspace" — it is
   * "unknown, and `packs` could not say". Consume only via {@link isFirmWorkspace}
   * (`src/workspace/useWorkspaceKind.ts`), which treats this exactly like a null
   * `activeMembership`: a workspace's kind stays undetermined until either this membership
   * read succeeds or {@link isFirmWorkspace}'s narrow id-comparison stand-in can name it.
   */
  packsResolved?: boolean;
  /**
   * Why THIS membership exists, as the inviter stamped it, carried through the façade
   * since steward-app#1386 round 3 (`services/steward-app-api/src/handlers/me.ts`'s
   * `memberships` mapping). Same field as {@link PendingInvite.via}, on the membership
   * appcore mints rather than the invitation that preceded it — present here on a
   * membership whose invitation carried it, so it survives past acceptance and past a
   * page reload, which the invite copy alone does not (the invite row is gone once it
   * is accepted).
   *
   * ⚠️ **A label, never a grant**, exactly as {@link PendingInvite.via}'s own note says.
   * Access is this membership existing and being active; this may only select what a
   * surface SHOWS about it. `AdvisorClients` reads it to keep its waiting-copy honest
   * after a reload, never to decide what the reader may open.
   *
   * ⚠️ **Absent on every membership minted before appcore#492 (2026-08-31), and against
   * a façade older than this round.** A consumer that reads it must degrade gracefully
   * to "unknown", never to "not waiting".
   */
  via?: MembershipVia;
}

export interface PendingInvite {
  token: string;
  workspaceId: string;
  tenantId: string;
  roleIds: string[];
  /** The inviter's opaque appcore userId, or an operator actor label for a
   *  seed-issued invite. **Never render it** — see {@link invitedByName}. */
  invitedBy?: string;
  /** The invited workspace's display name, resolved by appcore (#662). Optional:
   *  against an appcore that predates it the card degrades to the older, nameless
   *  copy rather than rendering `undefined`. */
  workspaceName?: string;
  /** The inviter's display name, resolved by appcore (#662). Never an email address
   *  and never an id: absent when the inviter is an operator label, has no profile,
   *  or has set no name, and the card then shows no inviter line at all. */
  invitedByName?: string;
  /**
   * Why this invitation exists, as the INVITER stamped it (#1312). Carried from appcore
   * through the façade's `/me` so a surface can tell an advisor-channel invitation from
   * an ordinary one BEFORE it is accepted.
   *
   * ⚠️ **A label, never a grant, and no gate in this SPA may read it.** appcore stores
   * and returns it without interpreting it, and whoever holds `mem:manage` in the
   * inviting workspace decides what it says, so it is unverified inviter-supplied text.
   * It may select what a surface SHOWS and nothing else; what the accepting user may
   * then do comes entirely from the membership appcore mints at acceptance.
   *
   * ⚠️ **Absent on every invitation issued before appcore#492 (2026-08-31)**, and absent
   * against a façade older than #1312, so a filter written on this field alone hides
   * older invitations. See `AdvisorClients`' `isAdvisorConnectionInvite`, which pairs it
   * with a `roleIds` test for that reason.
   */
  via?: MembershipVia;
  /**
   * Present only on a SPONSORED invitation (`via.kind === 'sponsorship'`, steward-app#1488):
   * what the acceptance disclosure needs. `firmName` is `null` when the façade could not
   * resolve the firm, and the card then offers no Accept. `disclosureVersion` is the copy
   * version the façade's accept requires back; the card renders the words for exactly that
   * version (`sponsoredDisclosure.ts`) or offers no Accept. It is ABSENT when the façade could
   * not state this setup's terms, and the card offers no Accept then either.
   *
   * `startAreas` and `lockedAreas` (steward-app#1971) come with the `sponsored-accept-v3`
   * version only: the area ids the firm's advisors start with, and the ones the sponsorship
   * keeps on. The card's sentences name them, and the accept sends both back.
   *
   * ⚠️ Display only, like `via`. The server refuses a sponsored accept that does not carry the
   * version (and, for v3, both lists as it would project them), and what the family may do
   * afterwards is the membership appcore mints.
   */
  sponsorship?: { firmName: string | null; disclosureVersion?: string; startAreas?: string[]; lockedAreas?: string[] };
}

/**
 * Provenance an inviter stamped on a membership or an invitation (appcore's
 * `MembershipViaSchema`; mirrored in the façade as `AppcoreMembershipVia`).
 *
 * ⚠️ **Unverified and non-authoritative.** `firmId` is steward-app's own opaque firm
 * slug and appcore never resolves it against anything, so a `firmId` here is a claim
 * about which firm asked, never proof of one. Render from it; never permit from it.
 */
export interface MembershipVia {
  kind: 'advisor-connection' | 'sponsorship';
  firmId: string;
}

/** The AI consent versions, the scope type and the acknowledgement rule live in
 *  `@steward-app/steward-types`' `aiConsent.ts` since steward-app#2542, because the façade now
 *  enforces the same rule. Re-exported here so the SPA's imports are unchanged; read an
 *  acknowledgement through `hasAiConsent`, never by hand. */
export {
  AI_CONSENT_VERSION, AI_FIRM_CONSENT_VERSION, AI_CONSENT_REQUIRED_CODE, hasAiConsent, type AiConsentScope,
} from '@steward-app/steward-types';

/** Per-user settings persisted on the profile (via /me) so they follow the user
 *  across devices. Steward-app's slice of appcore's opaque preferences blob. */
export interface UserPreferences {
  /** Workspace ids where the user has dismissed the getting-started checklist (FR2). */
  firstRunDismissed?: string[];
  /** ISO timestamp of when the user acknowledged the AI assistant consent notice
   *  (docs/15-ai-consent-privacy.md). Only a non-empty string counts as acknowledged —
   *  `null`/absent means not acknowledged. */
  aiConsentAcknowledgedAt?: string | null;
  /** {@link AI_CONSENT_VERSION} at acknowledgment time. Absent = 1 (acks predating the field). */
  aiConsentVersion?: number | null;
  /** The firm-workspace pair (steward-app#2523). A firm user is shown the firm variant of the
   *  notice once, whatever they accepted in a family workspace, and the reverse: neither pair
   *  satisfies the other. Absent = not acknowledged (there are no acks predating this field,
   *  so unlike {@link aiConsentVersion} an absent version is not read as 1). */
  aiFirmConsentAcknowledgedAt?: string | null;
  /** {@link AI_FIRM_CONSENT_VERSION} at acknowledgment time. */
  aiFirmConsentVersion?: number | null;
  /** ISO timestamp of when the user agreed to the credential vault's terms
   *  (steward-app#2011). Stamped by the façade (`PUT /me` with `credentialConsent`), never by
   *  the SPA: `PUT /me` refuses this key under `preferences`. Read it through `hasCredentialConsent`. */
  credentialConsentAcceptedAt?: string | null;
  /** `CREDENTIAL_CONSENT_VERSION` at agreement time. Façade-written, like the timestamp. */
  credentialConsentVersion?: number | null;
  /** ISO timestamp of when the user accepted the Terms of Service and Privacy Policy
   *  (steward-app#2630). Façade-stamped (`PUT /me` with `termsAcceptance`) and refused under
   *  `preferences`, like the vault agreement. Read it through `hasTermsAcceptance`. */
  termsAcceptedAt?: string | null;
  /** `TERMS_VERSION` at acceptance time. Façade-written, like the timestamp. */
  termsVersion?: number | null;
  /** ISO timestamp of when the user first saw the arrival welcome overlay
   *  (src/firstrun). Absent = they haven't arrived yet; set once, then the
   *  overlay never replays on its own (`?welcome=1` re-opens it on demand). */
  welcomeSeenAt?: string | null;
  /** Per-thread read state for the feedback unread indicator (src/feedback):
   *  `feedbackId` → the ISO `at` of the newest operator reply the user has seen
   *  in that thread. An absent id means every operator reply in it is unread.
   *
   *  Server-side rather than localStorage on purpose: localStorage is keyed by
   *  browser origin, not by user, so on a shared family browser one member's
   *  read state would suppress another's badge.
   *
   *  Appcore shallow-merges only TOP-LEVEL preference keys, so a write of this
   *  key replaces the whole map wholesale — which is what makes pruning
   *  possible (see `pruneFeedbackSeen`). There are no key-delete semantics, so
   *  nothing may depend on removing the key itself. */
  feedbackSeen?: Record<string, string>;
  /** ISO `publishedAt` of the newest announcement the person has had rendered on the
   *  Announcements page (steward-app#2612, docs/42 §5). Not "now": a stale fetch must not mark
   *  unseen an item that was never shown. Absent = 30 days ago, so launch-time users see the
   *  October backfill badged. Server-side for the reason `feedbackSeen` is. */
  announcementsSeenAt?: string | null;
  /** Announcements whose popup the person has closed (steward-app#2613, docs/42 §6): id → ISO
   *  instant. Capped at 10 entries, oldest pruned (`markPopupDismissed`). Whole-value
   *  replacement on write, like `feedbackSeen`. */
  announcementPopupDismissed?: Record<string, string>;
  /** "Remind me later" on the Dashboard's onboarding request card (steward-app#1427):
   *  `workspaceId` → `firmId` → when the person pressed it (browser clock) and the request's
   *  `lastIssuedAt` they put off (server value). Same whole-value replacement as
   *  `feedbackSeen`, so every write is pruned; `src/onboardingrequest/requests.ts` owns the shape,
   *  the three-day window and the pruning, and narrows whatever it reads back. */
  onboardingSnoozed?: Record<string, Record<string, { snoozedAt: string; lastIssuedAt: string | null }>>;
  /** The silent onboarding tax-id copy's "done" list (crossmarc/appcore#739): entries
   *  `<workspaceId>#<firmWorkspaceId>#<subject>`, newest last, capped. A listed subject is never
   *  copied again, so a number someone clears stays cleared. Server-side for the reason
   *  `feedbackSeen` gives. `src/onboarding/client/taxIdCopy.ts` owns the shape. */
  onboardingTaxIdCopied?: string[];
  /** The onboarding self-link claim's "settled" list (steward-app#1851): entries
   *  `<workspaceId>#<firmWorkspaceId>`, or `<workspaceId>#*` once the person has cleared their
   *  own link in that workspace. A listed pair is never claimed again, so a link someone
   *  clears stays cleared. `src/onboarding/client/selfLinkClaim.ts` owns the shape. */
  onboardingSelfLinked?: string[];
}

/** GET /me — identity + every workspace the user belongs to + pending invites. */
export interface MeResponse {
  userId: string;
  email?: string;
  /** Full display name (footer / account menu). */
  name?: string;
  /** Preferred name — drives the greeting; falls back to the first word of `name`. */
  givenName?: string;
  /** Cross-device user settings (getting-started dismissal, …). */
  preferences?: UserPreferences;
  memberships: WorkspaceMembership[];
  invites: PendingInvite[];
  /**
   * The caller still holds an unconsumed signup-eligibility grant, so
   * `POST /onboarding/bootstrap` would succeed (appcore#346). This is the real
   * precondition for creating your own organization, and it is INDEPENDENT of
   * membership count — someone granted their own tenant can also be invited to a
   * relative's workspace, and accepting that invite does not spend the grant.
   * Absent against an appcore that predates the field; callers fall back to the
   * old `memberships.length === 0` inference.
   */
  canBootstrapTenant?: boolean;
  /**
   * The firms that have invited this address straight to their onboarding form
   * (crossmarc/steward-app#1764). Absent when there are none. A ROUTING LABEL only: it sends a
   * signed-in client with no workspace to `/onboarding` (`onboarding/client/isOnboardingClient.ts`)
   * and names the firm on the welcome page. It grants nothing, here or on the façade.
   */
  onboardingInvitations?: OnboardingInvitationSummary[];
  /** White-label brand documents keyed by tenantId (docs/17). Only tenants the
   *  caller belongs to that hold the entitlement + a brand record appear here. */
  branding?: Record<string, import('../brand/brand').BrandDocument>;
}

/** One entry of {@link MeResponse.onboardingInvitations}, as the façade projects it. */
export interface OnboardingInvitationSummary {
  firmWorkspaceId: string;
  /** The firm's display name, or `null` when the façade could not name it. */
  firmName: string | null;
  /** The firm's brand key, when the directory resolves the firm (steward-app#2770). With it the client's pages name
   *  the firm as the snapshot form does (`firmDisplayName`); absent, they use `firmName`. */
  brandKey?: string;
  invitedAt: string;
  /**
   * True when this firm's sponsorship has already set up Steward for the caller, so the form is
   * closed (steward-app#2465). A routing label only: the write relays keep their own check. Absent
   * from an older façade, which reads as open.
   */
  formClosed?: boolean;
}

/** The signed-in user's identity, surfaced by WorkspaceProvider for the greeting,
 *  account menu, and profile editor. */
export interface UserProfile {
  userId: string;
  email?: string;
  name?: string;
  givenName?: string;
  preferences?: UserPreferences;
}

/** POST /workspaces response (the "+ New workspace" action). Also the shape of
 *  POST /onboarding/bootstrap (the founder front door) — same clean fields. */
export interface CreateWorkspaceResponse {
  workspaceId: string;
  workspaceName: string;
  tenantId: string;
  slug: string;
}

/** POST /onboarding/bootstrap request (O1 founder flow) — creates the caller's org +
 *  first workspace. appType is fixed server-side to `custom-data` (the SPA never picks). */
export interface BootstrapFounderRequest {
  tenantName: string;
  tenantSlug: string;
  workspaceName: string;
  workspaceSlug: string;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  entityId: string;
  version: number;
}

/** Response from a "new version" upload-url request. */
export interface VersionUploadUrlResponse {
  uploadUrl: string;
  version: number;
  expectedVersion: number;
}

export interface DownloadUrlResponse {
  downloadUrl: string;
}

export interface DocumentAttributes {
  title: string;
  category: string;
  description?: string;
  filename: string;
  mimeType: string;
  fileSize: number;
  tags?: string[];
  /** Optional expiry date (YYYY-MM-DD). */
  expiresAt?: string;
  /** Optional containing folder (a document-folder id); absent = unfiled / top level. */
  folderId?: string;
  // s3Key is never exposed by the façade — it is not part of the SPA shape.
}

/** A document folder (DOC3/Q3); hierarchy via `parentFolderId` (absent = top level). */
export interface FolderAttributes {
  name: string;
  parentFolderId?: string;
  /**
   * Provenance marker for a folder the app made for its own purposes rather than at a
   * family's request. Two values:
   *
   *   - `'unfiled'` — the hidden folder that backs the root of the Documents page
   *     (steward-app#811), which `useFolderTree` filters out of the tree so those documents
   *     keep rendering exactly where they always did;
   *   - `'advisor-shared:<firmId>'` — the "Shared with «firm»" folder the façade creates for
   *     an advisor connection (steward-app#944). NOT hidden: the family is meant to see it,
   *     which is why `isUnfiledFolder` is an exact equality and not a prefix test.
   *
   * Resolution is by THIS field and never by `name`, and the comparison is EXACT; the rule
   * and the scar it comes from live in `@steward-app/steward-types`' `unfiledFolder.ts`.
   */
  systemKey?: string;
}

/** A document carries its current version number (versioned entity). */
export interface DocumentResource extends Resource<DocumentAttributes> {
  currentVersion: number;
  /**
   * Who created / last updated this document, as appcore's INTERNAL user ULID — the value
   * `GET /members` keys its roster on, and never the Cognito `sub` (the façade's
   * `caller-identity.ts` has the full account of why those are different namespaces).
   *
   * OPTIONAL on purpose, and it must stay optional. The SPA and the façade are separate
   * deploy artifacts that move independently (CLAUDE.md), so a bundle carrying this type can
   * be talking to an API build that predates the field. Every consumer treats "absent" as
   * "unknown author" and renders nothing, never a placeholder and never an error.
   *
   * Meaningful only joined against `GET /members` (see `src/lib/membersQuery.ts`). Read
   * today by the "Shared with «firm»" folder on the Documents pillar (steward-app#733).
   */
  createdBy?: string;
  updatedBy?: string;
}

/** One entry in a document's version history. */
export interface DocumentVersion {
  version: number;
  filename: string;
  mimeType: string;
  fileSize: number;
  uploadedAt: string;
  uploadedBy: string;
}

/**
 * Aggregation provenance shared by every entity a sync can write. `source` absent or
 * `'manual'` ⇒ user-owned & editable; any other value ⇒ provider-synced and read-only.
 */
export interface SyncProvenance {
  source?: string;
  externalId?: string;
  connectionId?: string;
}

/** True when a row originated from an external aggregation provider (not manual entry). */
export function isSynced(attrs: SyncProvenance): boolean {
  return !!attrs.source && attrs.source !== 'manual';
}

/**
 * One custodian-mirrored beneficiary designation on an account (steward-app#1428,
 * docs/29 tier 3). **No writer populates this today** — #1018 (the Altruist Open API
 * spike, P3) is what would eventually mirror a custodian's actual designations here.
 * `personId` references a `person` record in the same workspace.
 *
 * ⚠️ Not where onboarding intent lives: what a family says they WANT is captured on
 * the intake request artifact and handed to the advisor to implement at the
 * custodian, never written to an account. See docs/29 §"The second form, split".
 * `BeneficiaryOfPanel` (`src/components/BeneficiaryOfPanel.tsx`) reads this array and
 * has no edit form, by design — but it is NOT YET WIRED into any route: the person
 * page it belongs on is #1425, which had not landed when this was built. Built and
 * tested standalone, ready for #1425 to import.
 *
 * Server-managed on the façade: `handlers/accounts.ts` restores this key from the
 * stored row on every write via `SYNC_OWNED_ACCOUNT_KEYS`, so a client-supplied
 * value can neither forge nor accidentally omit it. Closes steward-app#1444.
 */
export interface AccountBeneficiaryDesignation {
  personId: string;
  kind: 'primary' | 'contingent';
  /** Percent share of this account, when the custodian discloses one. */
  sharePercent?: number;
  /** ISO date the mirror last confirmed this designation. */
  asOfDate?: string;
}

export interface AccountAttributes extends SyncProvenance {
  name: string;
  institution: string;
  accountType: string;
  currency: string;
  lastFourHint?: string;
  notes?: string;
  tags?: string[];
  ownerEntityId?: string;
  /** A family member (`person` id) as the owner, instead of an entity (steward-app#1936). */
  ownerPersonId?: string;
  /** Set by the server in place of the person id for a viewer without the Family area. */
  ownerPersonHidden?: boolean;
  /** Custodian-mirrored, never client-editable. See `AccountBeneficiaryDesignation`. */
  beneficiaries?: AccountBeneficiaryDesignation[];
}

export interface AccountConnectionAttributes {
  provider: string;
  providerItemId: string;
  institutionName: string;
  status: 'pending' | 'active' | 'error' | 'disconnected';
  lastSyncedAt?: string;
  /** Set when a sync is dispatched, cleared by the terminal write that ends it (#357).
   *  Present ⇒ a sync was started and has not reported back — see `isSyncing`, which is
   *  what the connections list renders "Syncing…" from. */
  syncStartedAt?: string;
  /** Legacy raw text; no longer written (#2062) and never rendered. */
  lastError?: string;
  /** `reconnect_required` | `shared_login` | `provider_unavailable` | `sync_failed`; unknown values are tolerated. */
  lastErrorCode?: string;
  /** ISO timestamp at which the institution's own permission for this connection ends, when
   *  the provider reports one (steward-app#2265). Read through `lib/consentEnd`. */
  consentExpiresAt?: string;
}

export interface AssetAttributes extends SyncProvenance {
  accountId?: string;
  name: string;
  assetClass: string;
  ticker?: string;
  currency: string;
  ownerEntityId?: string;
  /** A family member (`person` id) as the owner, instead of an entity (steward-app#1936). */
  ownerPersonId?: string;
  /** Set by the server in place of the person id for a viewer without the Family area. */
  ownerPersonHidden?: boolean;
  notes?: string;
  tags?: string[];
  /** 'avm' = eligible for automated RentCast refresh; 'stated' (or absent) = manual appraisals only. */
  valuationMode?: 'avm' | 'stated';
  // Structured property address (F5). An attribute of the property, shown and editable
  // for every real-estate asset whatever its valuation mode — not, as the former
  // free-text `address` string was, an input reachable only through automated
  // estimates. AVM still REQUIRES it; see `missingAvmAddressFields`.
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressState?: string;
  addressPostalCode?: string;
  addressCountry?: string;
  /** Dwelling kind, as one of `PROPERTY_TYPE_OPTIONS`' codes (steward-app#287). */
  propertyType?: string;
  /** Public-records property details, filled by the lookup and correctable by hand. */
  bedrooms?: number;
  bathrooms?: number;
  squareFootage?: number;
  lotSize?: number;
  yearBuilt?: number;
  lastSalePrice?: number;
  lastSaleDate?: string;
  annualPropertyTax?: number;
  // Provider-reported position, stored but server-managed (#392): account-sync writes it,
  // the façade never accepts it from here and restores it on update. Present on synced
  // holdings only — a manual asset's quantity lives in its lots. Read `currentQuantity`
  // instead of these; it resolves provider-vs-lots for you.
  quantity?: number;
  /** Cost of the whole position, not per unit. */
  totalCostBasis?: number;
  /**
   * The provider's own last price, in the PROVIDER'S units — dollars per unit for most
   * instruments, percent of par for a bond (#393). Shown as provenance beside a value we
   * cannot derive ourselves; it is NOT `currentPrice` and does not multiply out to the value.
   */
  providerPrice?: number;
  providerPriceAsOf?: string;
  // Enrichment fields — computed, not stored
  currentQuantity?: number;
  averageCostBasis?: number;
  currentPrice?: number;
  currentValue?: number;
  /** Unified "valued as of" — price date (priced) or latest valuation date (appraised). */
  currentValueAsOf?: string;
  priceAsOf?: string;
  /** Provider code of the stored close behind `currentPrice`; see `lib/priceAttribution.ts`. */
  priceSource?: string;
  unrealizedGain?: number;
}

/**
 * The body `POST /assets` answers with (steward-app#880, #886).
 *
 * The asset itself, plus an optional report on the child rows folded into the same request:
 * the opening LOT (quantity, purchase date, cost basis) and the opening VALUATION. `meta` is
 * present only when the caller sent at least one of them, and a `false` flag means the asset
 * stored and that child row did not — a 201, not an error, because an error status over a
 * stored asset is what left the add form open on top of one and turned the retry click into
 * a duplicate asset.
 *
 * Spread the `meta` off before putting the resource in the query cache: everything
 * downstream expects a plain `Resource<AssetAttributes>` there.
 */
export type CreatedAsset = Resource<AssetAttributes> & {
  /** Absent unless a lot or a valuation was sent. Each key is optional INDEPENDENTLY,
   *  because each is reported on only if it was asked for: a create carrying a lot alone
   *  answers with `{ initialLotSaved }` and says nothing about a valuation. All four
   *  combinations are reachable — since #886 a failed lot no longer skips the valuation
   *  attempt, so both flags can be false on one response. */
  meta?: { initialLotSaved?: boolean; initialValuationSaved?: boolean };
};

export interface AssetLotAttributes {
  assetId: string;
  quantity: number;
  effectiveDate: string;
  costBasis?: number;
  lotType: 'purchase' | 'sale' | 'adjustment';
  source?: string;
  externalId?: string;
  notes?: string;
}

export interface ValuationAttributes {
  assetId: string;
  date: string;
  marketValue: number;
  currency: string;
  source?: string;
  notes?: string;
}

/**
 * What `POST /valuations` answers with (steward-app#899, part 1).
 *
 * Since steward-app#888 a create for a date this holding already has a hand-entered value on
 * REPLACES that row rather than adding a second, and the row it replaces is not necessarily
 * the caller's own: another member may have recorded it hours earlier, and `valuation` is not
 * versioned from the caller's side, so their figure is gone with no conflict and no trace.
 * The façade compares the LAST WRITER of the stored row (appcore's `_ub`) against the caller
 * server-side and reports the one bit of it that is safe to say. The last writer and not the
 * creator, because the claim is about the figure that was destroyed: see
 * `services/sameDayValuation.ts` under "WHY `_ub` AND NOT `_cb`".
 *
 * `meta` follows the shape `CreatedAsset` above uses for `POST /assets`: an optional bag,
 * absent when there is nothing to report, so the ordinary response is still a plain
 * `Resource<ValuationAttributes>` to spread into the cache.
 *
 * ⚠️ The flag is PRESENT ONLY WHEN TRUE, which is the one difference from `CreatedAsset`'s
 * pair, and it matters to anyone reading it. Over there `false` means "asked for, and it
 * failed". Here the façade fails toward silence: it says nothing when the stored row carried
 * no usable author stamp, or the caller could not be resolved, because a false "someone else changed your
 * number" is far worse than saying nothing. So a `false` would have had to mean both "this was
 * your own row" and "we could not tell". Absent means "no claim", never "no, it was yours".
 *
 * NO AUTHOR travels with this, by design and not by omission. Saying THAT it happened needs no
 * name; saying WHO would need `GET /members`, which appcore gates on `mem:manage` (only
 * `steward-admin` holds it), so an ordinary member handed an id would resolve it to nothing on
 * screen. That is steward-app#796, open and blocked. Neither this type nor `Resource<A>`
 * should learn to carry one.
 */
export type SavedValuation = Resource<ValuationAttributes> & {
  meta?: { replacedAnotherMembersValue?: boolean };
};

/**
 * Which valuations a sync wrote. Re-exported from `@steward-app/steward-types` rather
 * than defined here, because the façade gates `PUT /valuations/{id}` on the SAME rule —
 * a second copy would let the page offer an Edit the API then refuses.
 *
 * Deliberately NOT `isSynced` above: a valuation's `source` is free text the family types
 * ("Appraisal", "Broker estimate"), so the general rule reads a hand-entered row as
 * provider-managed. See the shared module for the whole story.
 */
export { isAutomaticValuation } from '@steward-app/steward-types';

export interface LiabilityAttributes extends SyncProvenance {
  name: string;
  liabilityType: 'mortgage' | 'loan' | 'line-of-credit' | 'heloc' | 'other';
  currentBalance: number;
  currency: string;
  currentBalanceAsOf: string;
  notes?: string;
  tags?: string[];
  /** Containment — the account this liability lives inside (mirrors asset.accountId). */
  accountId?: string;
  /** Encumbrance — a specific asset this liability is secured against (orthogonal to accountId). */
  linkedAssetId?: string;
  obligorEntityId?: string;
  /** A family member (`person` id) as the obligor, instead of an entity (steward-app#1936). */
  obligorPersonId?: string;
  /** Set by the server in place of the person id for a viewer without the Family area. */
  ownerPersonHidden?: boolean;
  lender?: string;
  interestRate?: number;
  originalPrincipal?: number;
  maturityDate?: string;
  /**
   * When the debt was taken on (YYYY-MM-DD). Paired with `originalPrincipal`, it is what the
   * history counts as owed from that day until the first recorded balance; before it, the
   * liability is not part of the family's history at all (steward-app#254).
   */
  originationDate?: string;
}

/**
 * One thing that happened to one asset (docs/40-property-history.md, steward-app#2568).
 * `date` is `YYYY-MM` or `YYYY-MM-DD`: a month is the honest precision for "the roof went on
 * in summer 2014". `cost` is in the ASSET's currency; the row carries no currency of its own.
 */
export type AssetEventKind = 'installed' | 'replaced' | 'serviced' | 'repaired' | 'inspected' | 'other';

export interface AssetEventAttributes {
  assetId: string;
  date: string;
  kind: AssetEventKind;
  part?: string;
  title: string;
  notes?: string;
  cost?: number;
  improvement?: boolean;
}

/**
 * One day's balance of a liability (steward-app#254). The liability's own
 * `currentBalance`/`currentBalanceAsOf` is kept equal to the latest of these by the façade.
 * Rows a connection wrote carry its provenance and are the connection's to change.
 */
export interface LiabilityBalanceAttributes extends SyncProvenance {
  liabilityId: string;
  date: string;
  balance: number;
  currency: string;
  notes?: string;
}

export interface NetWorthSnapshotAttributes {
  date: string;
  netWorth: number;
  totalAssets: number;
  totalLiabilities: number;
  equitiesValue?: number;
  realEstateValue?: number;
  cashValue?: number;
  otherAssetsValue?: number;
  assetBreakdown?: Record<string, number>;
  liabilityBreakdown?: Record<string, number>;
  /**
   * Optional, and the compiler is the point. The calculator derives this from the holdings
   * it summed and OMITS it when nothing resolvable voted, rather than defaulting to a
   * currency the household does not use (steward-app#484) — so a call site that formats
   * `snap.currency` without deciding what absent means will not typecheck.
   *
   * Rows written before #484 carry the literal `'USD'`, whatever the household holds, and
   * #515 decided against a deliberate backfill: a scan of every environment found not one of
   * those labels actually wrong (every live workspace holds USD), so a migration would
   * rewrite 458 rows to the values they already hold.
   *
   * That is NOT the same as the old rows being frozen, and the difference is the trap here.
   * Any write that fires `triggerSnapshotBackfill` — twelve façade paths, covering assets,
   * lots, liabilities, valuations, accounts and connections — starts a recompute from that
   * write's date to today, and `recomputeHandler` force-overwrites every row in the range
   * through the post-#484 `computeSnapshot`. One back-dated correction therefore relabels a
   * whole stretch of a household's history, piecemeal, driven by ordinary use.
   *
   * So trust depends on when a row was last WRITTEN, and a row records only the date it is
   * keyed to. There is no deploy date, and no rule over `date`, that separates the two. A
   * present value is not evidence about the household unless you independently know that row
   * was recomputed after #484. Anything wanting a per-point label (a charted history, say)
   * has to reckon with a boundary that is per workspace, per date range, and moves over time.
   */
  currency?: string;
  computedAt: string;
  /**
   * Optional on the READ side only. The calculator always writes it (`calculator.ts`
   * falls back to the snapshot date when no market price was fetched), so its own
   * output type keeps this required — that is a guarantee the writer really makes.
   *
   * This type describes rows coming back out of storage, which were written by
   * arbitrarily old deploys of that calculator. The entity is `schemaMode: 'schemaless'`,
   * so the platform validates nothing on write, and no migration backfills existing
   * rows. Optional here is what makes the render guards in `Portfolio.tsx` load-bearing
   * to the compiler: `fmtDate` takes a plain `string`, so deleting a guard fails the
   * build rather than shipping a dateline that trails into an empty date (#454).
   */
  lastTradingDate?: string;
}

export interface NetWorthChange {
  amount: number;
  percent: number;
  /**
   * The range reaches further back than the recorded history, so this figure covers a
   * SHORTER span than its label implies. Present only when true.
   *
   * Caption it with `anchorDate` rather than the range name: presenting a three-month
   * move as "past year" is not a rounding error, it is a different claim.
   */
  truncated?: boolean;
  /**
   * The complement of `truncated` (steward-app#400): the anchor sits materially OLDER than
   * the range's target, so this figure covers MORE time than its label implies. Mutually
   * exclusive with `truncated` by construction. Caption it from `anchorDate` too — never
   * as "(partial)", which claims the wrong direction.
   */
  overExtended?: true;
  /**
   * The date this figure is ACTUALLY measured from, whenever an anchor was found
   * (steward-app#400). Use it to caption a `truncated` or `overExtended` range honestly
   * ("since <anchorDate>"). Also present on an unflagged range, and on `all`, where it
   * always equals `historyStart`.
   */
  anchorDate?: string;
  /**
   * No snapshot exists at or before this range's target: there is nothing to anchor to
   * (steward-app#400), most often a workspace younger than the window. `amount`/`percent`
   * stay 0 so the wire shape is unchanged for an older client, but they are not a real
   * answer — render "not enough history yet" instead of the figure when this is set.
   * Never set on `all`, which spans all VISIBLE history by definition.
   */
  insufficientHistory?: true;
}

export interface NetWorthResponse {
  current: Resource<NetWorthSnapshotAttributes>;
  changes: {
    '1W': NetWorthChange;
    '1M': NetWorthChange;
    '3M': NetWorthChange;
    '1Y': NetWorthChange;
    'YTD': NetWorthChange;
    'all': NetWorthChange;
  };
  /** First recorded date the figures above are anchored to. */
  historyStart: string;
  /** The household's starting point, when one is set. */
  baselineDate?: string;
}

export interface NetWorthHistoryResponse {
  items: Resource<NetWorthSnapshotAttributes>[];
  /** The household's starting point, when one is set (see NetWorthBaseline). */
  baselineDate?: string;
  /** First snapshot at or after `baselineDate` — where the visible series actually begins. */
  historyStart?: string;
}

/**
 * The household's net-worth starting point.
 *
 * Onboarding takes days or weeks, and the nightly snapshot totals whatever has been
 * connected or entered by then, so the early series records data-entry order rather than
 * market movement. Setting a starting point hides that stretch. It never deletes a
 * snapshot: clearing it brings the full history back.
 */
export interface NetWorthBaseline {
  baselineDate: string;
  setAt?: string;
  setBy?: string;
  reason?: string;
}

export interface NetWorthBaselineResponse {
  baseline: NetWorthBaseline | null;
}

/**
 * GET /dashboard/summary (PS6) — record counts per pillar + near-expiry
 * documents. Replaces the Dashboard's old pattern of fetching full,
 * unpaginated lists purely to derive a count or an expiry badge; see
 * `src/lib/useDashboardSummary.ts`.
 *
 * No accounts/assets/liabilities counts here — the Dashboard fetches those
 * pillars in full regardless (for the net-worth hero, allocation strip, and
 * largest-holdings list) and reads their `.length` from that fetch instead.
 */
export interface DashboardCounts {
  documents: number;
  entities: number;
  contacts: number;
  connections: number;
  members: number;
  invites: number;
}

/** A document with an expiry date, as surfaced by the summary endpoint (already
 *  filtered server-side to documents that carry an expiresAt at all). */
export interface DashboardAttentionDocument {
  id: string;
  title: string;
  expiresAt: string;
}

/** A connection that has stopped working, as surfaced by the summary endpoint (already
 *  filtered server-side to the non-healthy statuses). `lastSyncedAt` is how long the
 *  figures have been standing still; absent when it never synced at all. */
export interface DashboardAttentionConnection {
  id: string;
  institutionName: string;
  status: 'error' | 'disconnected';
  lastSyncedAt?: string;
}

export interface DashboardSummaryResponse {
  counts: DashboardCounts;
  attention: {
    documents: DashboardAttentionDocument[];
    connections: DashboardAttentionConnection[];
  };
}

export interface LegalEntityAttributes {
  name: string;
  entityType: string;
  jurisdiction?: string;
  taxIdHint?: string;
  status: string;
  notes?: string;
}

/** A family contact (CT1) — doctors, lawyers, CPAs, advisors, … with user-owned notes/tags. */
export type ContactCategory =
  | 'doctor' | 'lawyer' | 'accountant' | 'financial-advisor' | 'insurance' | 'estate' | 'other';

export interface ContactAttributes {
  name: string;
  category: ContactCategory;
  organization?: string;
  phone?: string;
  email?: string;
  address?: string;
  website?: string;
  notes?: string;
  tags?: string[];
}

/** Category display labels + canonical option order for the Contacts UI. */
export const CONTACT_CATEGORIES: { value: ContactCategory; label: string }[] = [
  { value: 'doctor',            label: 'Doctor' },
  { value: 'lawyer',            label: 'Lawyer' },
  { value: 'accountant',        label: 'Accountant / CPA' },
  { value: 'financial-advisor', label: 'Financial advisor' },
  { value: 'insurance',         label: 'Insurance' },
  { value: 'estate',            label: 'Estate' },
  { value: 'other',             label: 'Other' },
];

/**
 * The professional-advisor categories (steward-app#1932): the contacts People → Advisors lists
 * under "Not on Steward". Every other category (doctor, other) stays on "Other contacts".
 *
 * ⚠️ **A FILTER over `contact.attributes.category`, not a data migration.** No row is moved or
 * rewritten, and the contacts API is unchanged: a contact added years ago as an accountant
 * simply appears on the Advisors tab the day this ships. Both tabs and both add forms read this
 * one list, so a category cannot end up on neither tab or on both.
 *
 * Membership is pinned in `types.contactCategories.test.ts`, in both directions (each is a real
 * `ContactCategory`, and the complement is exactly doctor and other).
 */
export const ADVISOR_CONTACT_CATEGORIES: ContactCategory[] = [
  'financial-advisor', 'accountant', 'lawyer', 'estate', 'insurance',
];

export const CONTACT_CATEGORY_LABEL: Record<ContactCategory, string> =
  Object.fromEntries(CONTACT_CATEGORIES.map(c => [c.value, c.label])) as Record<ContactCategory, string>;

/**
 * A member of the household (steward-app#1260/#1425) — the People pillar's Family tab.
 *
 * Beside {@link ContactAttributes}, never merged with it: a contact is an address-book entry
 * for somebody the family DEALS WITH, a person is somebody the estate is FOR. The authority
 * for the shape is the `person` entry in `STEWARD_APP_TYPE_DEFS`
 * (`packages/steward-types/src/index.ts`); this mirrors it for the SPA.
 *
 * ⚠️ **The tax identification number itself is NOT here and never will be.** #1260's tier
 * stores it envelope-encrypted inside appcore, under a dedicated CMK, and the only thing
 * that decrypts it is one audited reveal at a time (`GET /people/{id}/tax-id/reveal`). What
 * this shape carries is {@link PersonAttributes.taxIdHint}, the masked last four. A revealed
 * number lives in transient component state and nowhere else: never in a query cache, never
 * in storage, and never merged back into a `Resource<PersonAttributes>`.
 */
export interface PersonAttributes {
  /** First and last only (steward-app#1564): no middle name, no suffix. Both optional on
   *  the type — a legacy row may carry only {@link PersonAttributes.legalName} — but the
   *  SPA's People form and the intake prefill always set both together and derive
   *  `legalName` from them client-side; see that field's own doc for why the derivation is
   *  client-side and not appcore's `derived: true`. */
  firstName?: string;
  lastName?: string;
  /** The only REQUIRED field, and the roster's display fallback for a record with no split
   *  name on file. Client-derived from {@link PersonAttributes.firstName} and
   *  {@link PersonAttributes.lastName} on every write that touches identity (steward-app#1564)
   *  — never appcore `derived: true`, so a client omitting it still gets an ordinary
   *  missing-required-field rejection. Existing rows written before #1564 may carry only
   *  this field; every reader falls back to it. */
  legalName: string;
  /** What the household actually calls them. A DIFFERENT field from the preferred name on
   *  the signed-in user's own account in Settings, deliberately: two records, one label. */
  preferredName?: string;
  /** ISO date (`YYYY-MM-DD`). */
  dateOfBirth?: string;
  /** Free text, never an enum: a fixed list is what forces somebody to misfile a stepson,
   *  a ward, a godmother, or the aunt who raised them. The form offers suggestions, which
   *  is not the same control as a select. */
  relationship?: string;
  /** Optional plain text (#1957), as on a contact. Filled from onboarding for a spouse or an
   *  adult child who is the trusted contact; never for the account holder's own row. */
  phone?: string;
  email?: string;
  legalAddress?: string;
  /** Captured only when it differs from the legal address; left empty otherwise, so "these
   *  are the same" stays a fact the record states once. */
  mailingAddress?: string;
  /** A reference to a `contact` record, so People does not grow a second address book. */
  trustedContactId?: string;
  /** Whether this record is linked to a login (steward-app#1851). The façade sends a fixed
   *  marker (`PERSON_LINKED_MARKER`, `#linked`) when it is and omits the key when it is not.
   *  It is never a user id, so it cannot say WHOSE record this is: the viewer's own record
   *  comes from `GET /people/self`, and a member's record from `Member.personId`. The stored
   *  value is written only by appcore's self-link routes, always with the caller's own id.
   *  The façade drops the key from every generic person write; `personWriteAttributes` also
   *  strips it on the person page's edit path.
   *
   *  ⚠️ **This confers no access and is never consulted for any.** Access comes only from a
   *  membership or an advisor connection, both administered under People → Access (docs/04
   *  §3.3). All this field does is let the two lists name each other. Setting it grants
   *  nothing; clearing it revokes nothing. */
  linkedUserId?: string;
  notes?: string;
  tags?: string[];
  /**
   * The LAST FOUR digits of the stored tax identification number, and nothing else
   * (steward-app#1260). Absent when no number is stored, which is how a caller tells
   * "none on file" from "one on file" without decrypting anything.
   *
   * ⚠️ **READ ONLY. Never send this field on a person write.** It is declared
   * `derived: true` on the `person` type, so appcore answers a client-supplied value with a
   * `400 DERIVED_ATTRIBUTES` on its generic create and update alike, whatever capabilities
   * the caller holds. A read-modify-write that spreads the whole attribute bag back up
   * therefore breaks EVERY person edit, which is exactly how this field is easiest to get
   * wrong. appcore carries the stored hint forward when a write omits it, so omitting it is
   * both correct and sufficient.
   *
   * The only writer is `PUT /people/{id}/tax-id`, which derives it server-side from the
   * plaintext it just sealed, so the mask is a true suffix of what is stored.
   */
  taxIdHint?: string;
  /**
   * The onboarding stamps (crossmarc/appcore#739): `'client'` or `'spouse'`, and
   * `<firmWorkspaceId>#<clientId>`. Written only by the client prefill, at create, on the rows it
   * made for the client and spouse. `createOnly`: never send either back (`personWriteAttributes`
   * strips them). Read by the silent tax-id copy to find its target; never shown.
   */
  onboardingSubject?: string;
  onboardingSource?: string;
}

/**
 * Relationship words the person form offers as suggestions.
 *
 * ⚠️ **Suggestions, never options.** `relationship` is free text (see above), and turning
 * this into a `<select>` is a product decision that would make a family choose the nearest
 * wrong word for someone they love. Rendered through a `<datalist>`, so anything typed is
 * kept exactly as typed.
 */
export const RELATIONSHIP_SUGGESTIONS: readonly string[] = [
  'Spouse', 'Partner', 'Son', 'Daughter', 'Child', 'Parent', 'Sibling', 'Grandchild', 'Grandparent',
];

/**
 * Credential vault (PV2). Metadata only — the secret is NEVER part of this shape.
 * The cleartext is supplied on create/edit and obtained back only via the reveal
 * endpoint, one at a time. Never persist a revealed secret to storage/state.
 */
// The values are `@steward-app/steward-types`' `CREDENTIAL_CATEGORY_VALUES`, which the façade
// validates a write against. `card` and `account-number` were removed on 2026-10-02
// (steward-app#2011): Steward is not intended to hold payment card data, and the vault is
// positioned for logins and secure notes.
export type { CredentialCategory };

export interface CredentialAttributes {
  label: string;
  category: CredentialCategory;
  username?: string;
  url?: string;
  notes?: string;
  tags?: string[];
  ownerEntityId?: string;
  vaultId?: string;
  folderId?: string;
}

export const CREDENTIAL_CATEGORIES: { value: CredentialCategory; label: string }[] = [
  { value: 'login',       label: 'Login' },
  { value: 'secure-note', label: 'Secure note' },
  { value: 'other',       label: 'Other' },
];

export const CREDENTIAL_CATEGORY_LABEL: Record<CredentialCategory, string> =
  Object.fromEntries(CREDENTIAL_CATEGORIES.map(c => [c.value, c.label])) as Record<CredentialCategory, string>;

/** A credential vault (CV4) — an access-controlled collection of credentials. */
export interface CredentialVaultAttributes {
  name: string;
  description?: string;
}

/** An entry on an access-controlled target's ACL. principalId is a userId (USR) or
 *  roleId (ROLE). Shared by credential vaults (verb `reveal`) and document folders
 *  (verb `read`) — both ride the platform's generic instance-ACL API. */
export interface AclEntry {
  targetId: string;
  principalType: 'USR' | 'ROLE';
  principalId: string;
  verbs: string[];
  expiresAt?: string | null;
}

/** @deprecated use {@link AclEntry} — kept as an alias for the CV4 call sites. */
export type VaultAclEntry = AclEntry;

/** An ownership-graph edge (DOC2/DE2). `toType` is an appcore typeCode
 *  ('asset' | 'account' | 'legal-entity' | 'document'). */
export interface Link {
  id: string;
  relation: string;
  fromType: string;
  fromId: string;
  toType: string;
  toId: string;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

/** Canonical link relations; mirrors the appcore registry. `associatedWith` is
 *  symmetric (its own inverse) — the neutral document↔holding association edge. */
export const LINK_RELATIONS = ['owns', 'ownedBy', 'references', 'referencedBy', 'uses', 'usedBy', 'associatedWith'] as const;

// Role IDs from the steward-app App Pack seed (scripts/seed-steward-app.ts).
export const STEWARD_ROLES = STEWARD_ROLE_IDS;

// A Map, not an object literal — load-bearing, not stylistic. Every read site indexes
// this by a caller-supplied roleId: ACL `principalId` is bare `z.string().min(1)`
// (handlers/folderAcl.ts, handlers/vaultAcl.ts) and invite `roleIds` is bare
// `z.array(z.string()).min(1)` (handlers/invites.ts) — appcore's own role/user ids are
// ULIDs, not any of these names, but nothing stops a caller from POSTing the literal
// string "constructor". Looked up in an object literal that resolves through
// Object.prototype to a FUNCTION rather than undefined, so `ROLE_LABELS[id] ?? fallback`
// never falls back (a function is not nullish) and `SelectWorkspace.tsx`'s
// `.join(', ')` string-coerces the function straight into the page. See
// src/lib/sourceLabel.ts and crossmarc/steward-app#547.
export const ROLE_LABELS: ReadonlyMap<string, string> = new Map<string, string>([
  [STEWARD_ROLES.ADMIN, 'Admin'],
  [STEWARD_ROLES.MEMBER, 'Member'],
  [STEWARD_ROLES.VIEWER, 'Viewer'],
  // steward-app#643 — display only. Deliberately NOT in `ROLE_OPTIONS` below: an advisor
  // connection's role is pinned server-side by `handlers/advisor.ts`'s `advisorRoleIds()`
  // and must stay that way, so this id is never offered in the ordinary member-role
  // picker. Without this entry, `getRoleLabel` in Access.tsx falls through to a raw
  // ULID prefix for a connected advisor's row.
  [STEWARD_ROLES.ADVISOR, 'Advisor'],
  // steward-app#742 step 4 — display only, and never in `ROLE_OPTIONS`, for exactly the
  // reason `ADVISOR` above is not: it is composed server-side by the shared-folder reconcile
  // and offering it in the member-role picker would be a way to hand somebody `inst:write`
  // outside the one folder the confinement covers. Without this entry a contributing
  // advisor's row in Members renders as a raw ULID prefix, and `SelectWorkspace.tsx`'s
  // `ROLE_LABELS.get(id) ?? 'Member'` prints the false claim "Member".
  //
  // The label says what the role does rather than naming the mechanism: a family reading
  // their own Members list should not have to know the word "confined".
  [STEWARD_ROLES.ADVISOR_CONTRIBUTOR, 'Advisor: can add documents'],
  // steward-app#739 — the four sharing-pillar roles, display only, and NOT in
  // `ROLE_OPTIONS` for the same reason `ADVISOR` is not: they are pinned server-side per
  // connection, never picked in the member-role picker (#650 was that bug).
  //
  // Added in phase 1 ahead of anybody holding one, because the fallbacks are actively
  // WRONG rather than merely blank: `SelectWorkspace.tsx` uses `ROLE_LABELS.get(id) ?? 'Member'`,
  // which would render an advisor's row as "Advisor, Member, Member, Member", and
  // `Access.tsx` falls through to a raw ULID prefix. Same trap the ADVISOR entry above
  // records from #643.
  //
  // ⚠️ **Real memberships carry these since phase 2a**, so these labels now RENDER. See
  // `visibleRoleIds` below: while `sharingAreasEnabled()` is off they are hidden from every
  // role list. Phase 2b armed the capabilities, so "Advisor, Shared: Portfolio, Shared:
  // Documents" is a TRUE claim about what the firm can reach for every ARMED pillar — the
  // flag, not the truth of the label, is what hides it where it is off.
  //
  // ⚠️ Mapped over the TOTAL `SHARING_PILLARS`, never `LIVE_SHARING_PILLARS`. A label is
  // needed for any role a membership can CARRY, and an in-flight pillar role is still a real
  // id — the fallbacks here are actively wrong rather than blank, which is the whole reason
  // this block exists.
  //
  // ⚠️ The sentence that used to end this note — "nothing composes such a role today, so no
  // such label can render yet" — was only ever about an IN-FLIGHT pillar role, and it now
  // reads as though the newest label is unreachable. It is not: steward-app#1260 phase 2
  // armed `people` on 2026-09-14, `PILLAR_GRANTED_NOT_YET_ARMED` is empty, and
  // `Shared: Family` renders like the other four. The claim survives only in its narrow
  // sense, which is that no label for a pillar that is granted-but-unarmed can render while
  // there is no such pillar.
  ...SHARING_PILLARS.map((p): [string, string] => [p.roleId, `Shared: ${p.label}`]),
  // steward-app#1260 — the `people` pillar's SECOND role
  // (`SharingPillar.additionalRoleIds`), display only, and required rather than optional: the
  // coverage guard in `types.test.ts` is total over every declared pack role def, so a role
  // seeded without a label here reds instead of shipping the actively-wrong fallbacks the
  // block above records (a raw ULID prefix in `Access.tsx`, the false claim "Member" in
  // `SelectWorkspace.tsx`).
  //
  // It is spelled out rather than derived because `additionalRoleIds` carries ids and nothing
  // else — no label to map over — which is deliberate (`pillars.ts` says why the reveal
  // capability must stay out of the pillar table's shape).
  //
  // ⚠️ **No comma in the label, on purpose.** These render comma-joined
  // (`SelectWorkspace.tsx`'s `.join(', ')`), so "Shared: Family, tax identification" would
  // read as two roles. "Shared: Family, Shared: Tax identification" is what a family sees
  // once both are composed, and it says the true thing: the firm can see the Family area,
  // and can reveal a tax identification number.
  //
  // Hidden by `visibleRoleIds` along with the other five while `sharingAreasEnabled()` is
  // off, with no edit there: that filter is built from `PILLAR_ROLE_IDS`, which spreads
  // `additionalRoleIds`.
  [PEOPLE_TAX_ID_REVEAL_ROLE_ID, 'Shared: Tax identification'],
  // steward-app#1950 — the documents pillar's OFF role (`SharingPillar.offRoleIds`): composed
  // when a family switches Documents off, and it reads only the folder shared with that firm.
  // Same no-comma rule as the entry above, and the same `visibleRoleIds` hiding (it is in
  // `PILLAR_ROLE_IDS`). The label says what the firm can reach, not that an area is off.
  [DOCUMENTS_SHARED_FOLDER_ROLE_ID, 'Shared: Documents (shared folders only)'],
  // steward-app#2581 — the four cash flow roles (`cashFlowAccess.ts`), display only, and
  // required: the coverage guard in `types.test.ts` is total over every declared pack role.
  // Nothing composes any of them yet. Same no-comma rule as above. The two FAMILY viewer
  // roles say what that person sees; the two FIRM roles use the "Shared:" prefix the firm's
  // other composable roles use, and are hidden by `visibleRoleIds` like them.
  [CASH_FLOW_ROLE_IDS.FAMILY_TOTALS, 'Cash flow: totals'],
  [CASH_FLOW_ROLE_IDS.FAMILY_DETAIL, 'Cash flow: every transaction'],
  [CASH_FLOW_ROLE_IDS.FIRM_TOTALS, 'Shared: Cash flow totals'],
  [CASH_FLOW_ROLE_IDS.FIRM_DETAIL, 'Shared: Cash flow transactions'],
  // steward-app#1106 — display only, same trap the ADVISOR (#643) and pillar (#739)
  // entries above record: without it, `getRoleLabel` in Access.tsx falls through to a
  // raw ULID prefix and `SelectWorkspace.tsx`'s `ROLE_LABELS.get(id) ?? 'Member'` prints
  // the false claim "Member" for the one role that carries no `inst:*` at all
  // (`NO_WRITE_ROLE_IDS`, steward-app#1102). "Firm authority" matches the pack role's own
  // `name` (`packRoles.ts`) and states plainly the one thing a holder can do: bind a
  // household onto the firm's roster.
  //
  // Deliberately NOT filtered out of a client workspace's roster by `visibleRoleIds`
  // below, unlike the pillar roles. Those are hidden because the flag genuinely
  // withholds them; this role has no such flag; and per #1102 the one shape where it
  // could appear on a client-workspace membership is already a mis-landed grant, not an
  // intended one. Hiding the label would hide the mistake from the family whose
  // workspace it is, which is worse than an unfamiliar role name in their Members list.
  [STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_AUTHORITY, 'Firm authority'],
  // crossmarc/appcore#501 — display only, and the same trap the ADVISOR (#643) and pillar
  // (#739) entries above record: without an entry here `Access.tsx` falls through to a raw
  // ULID prefix and `SelectWorkspace.tsx`'s `ROLE_LABELS.get(id) ?? 'Member'` prints the
  // false claim "Member".
  //
  // This role IS assignable through the ordinary member and invite paths (`scope:
  // 'workspace'`, see `packRoles.ts`), so it shows up on real firm-workspace memberships as
  // soon as a firm admin grants it. (That sentence used to open "Unlike FIRM_AUTHORITY just
  // above and every other entry in this map"; steward-app#1343 made FIRM_AUTHORITY
  // workspace-scoped on 2026-09-09, so the two are alike in this respect now.) "Firm staff" matches the pack role's own `name` and says the only thing
  // a holder can do: see the firm's client roster.
  //
  // ⚠️ It is deliberately NOT added to `ROLE_OPTIONS` below, and the reason still holds even
  // though the consequence no longer does. `ROLE_OPTIONS` is ONE global list with no notion of
  // which workspace is open, so adding this id would offer "Firm staff" in every family's
  // member picker — on a client workspace the role means nothing and granting it there is
  // precisely the mis-landed grant `roles.ts` warns about.
  //
  // ⚠️ **What this comment claimed the cost was is now FALSE, and has been since #1122.** It
  // said "a firm admin cannot grant this through the Members UI today: the grant goes through
  // the façade's `PUT /members/{userId}` (or an operator write) until the picker learns which
  // workspace it is rendering in". `Access.tsx` has had a **Firm staff checkbox** since
  // crossmarc/steward-app#1122, composed at render time and gated on `useWorkspaceKind() ===
  // 'firm'`, and steward-app#1343 added an **Accepts clients** checkbox beside it the same
  // way. So a firm admin grants and revokes both roles in-app, **on every member row
  // including their own** — the checkboxes deliberately do NOT inherit the role picker's
  // `isSelf || lockedLastAdmin || isAdvisor` guard, because those three protect the ordinary
  // role and a firm-role checkbox never touches it. (They DID share it until the second
  // security review of #1343: a firm workspace with one membership then rendered no checkbox
  // at all and could never arm itself. `Access.tsx` carries the full account.) What is still
  // true is that the PICKER itself stays workspace-unaware, which is why these are checkboxes
  // rather than picker options. Making the picker workspace-aware remains the tidier fix and remains out
  // of scope. The label below is what keeps a granted role from rendering as "Member".
  [STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_STAFF, 'Firm staff'],
  // crossmarc/appcore#645 — same trap, same fix: without a label the role renders as a raw
  // ULID prefix in `Access.tsx` and as "Member" in `SelectWorkspace.tsx`. Matches the pack
  // role's `name`. Workspace-scoped and granted by a firm admin's checkbox, like the two above,
  // and for the same reason NOT in `ROLE_OPTIONS`.
  [STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_ROSTER, 'Firm roster'],
  // steward-app#1481 — same trap, same fix, for `steward-firm-provision`. Matches the pack role's
  // `name` ("Firm Client Setup"). Granted by "Your firm"'s invite and a firm admin's checkbox,
  // and for the same reason as the three above NOT in `ROLE_OPTIONS`.
  [STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_PROVISION, 'Firm client setup'],
  // ⚠️ **appcore's own PLATFORM workspace roles, and steward-app#835 is what made their
  // absence a live defect rather than a latent one.** Same trap as every entry above: with no
  // label, `FolderAclManagerModal.tsx`'s `roleAclLabel(id, shortId(id))` renders a raw
  // 8-character ULID prefix, and `01HXRR0LE00QR5F9KA2B7CY3NW` and `01HXRR0LE00WSEDITOR0000001`
  // share ALL EIGHT, so two entries render as the identical chip "01HXRR0L… (role)", each with
  // its own remove button.
  //
  // Why #835 changed this: `familyRoleIds` (`services/steward-app-api/src/handlers/advisor.ts`)
  // now SEEDS `workspace-operator` and `workspace-editor` onto every folder a family marks
  // "Only your family" and onto every advisor shared folder, whether or not anybody holds
  // them. So these two are on the access list a family opens, in every such workspace, rather
  // than only where the harvest happened to pick one up. Found in that PR's security review.
  //
  // `workspace-viewer` is labelled beside them because the harvest can put it on the same list
  // and an unlabelled chip between two labelled siblings reads as a bug in the dialog. The two
  // platform ADMIN roles are harvestable too and are deliberately left for a separate change:
  // they need a label that does not collide with steward `ADMIN` in the member-role surfaces,
  // which is a wider question than this list.
  //
  // The labels name appcore's own role names (`packages/core/src/entities/role.ts`) rather
  // than inventing plainer ones, for the reason the FIRM_AUTHORITY entry gives: a family who
  // was granted one of these was granted it BY NAME, through Settings → Members or an invite,
  // and a renamed label would leave them unable to match the chip to the choice they made.
  // "Workspace" is the word the product already uses for the thing a family's people belong
  // to. Display only, and NOT in `ROLE_OPTIONS`: what the member-role picker offers is a
  // separate decision from what a granted role is called.
  [PLATFORM_WRITER_ROLE_IDS.WORKSPACE_OPERATOR, 'Workspace operator'],
  [PLATFORM_WRITER_ROLE_IDS.WORKSPACE_EDITOR, 'Workspace editor'],
  [PLATFORM_READ_ONLY_ROLE_IDS.WORKSPACE_VIEWER, 'Workspace viewer'],
]);

/**
 * `${role label} (role)` for an ACL principal row (`Credentials.tsx`'s and
 * `FolderAclManagerModal.tsx`'s `principalLabel`). Extracted so the `ROLE_LABELS.get`
 * lookup used at both read sites is exercised by a test rather than only hand-verified —
 * see `ROLE_LABELS`'s own comment for the Object.prototype hazard this closes.
 *
 * Takes the "unknown id" fallback as a parameter: both call sites currently pass the
 * shortened id from `shortUserId` (`utils/memberIdentity.ts`), and this function does not
 * compute it.
 */
export function roleAclLabel(principalId: string, fallback: string): string {
  return `${ROLE_LABELS.get(principalId) ?? fallback} (role)`;
}

/**
 * A membership's role ids, filtered to the ones a family should SEE in a role list.
 *
 * ⚠️ **This is the second half of the flag, and without it the first half leaks.**
 * `sharingAreasEnabled()` hides the People → Advisors control, but the façade composes the
 * pillar roles onto real memberships regardless (phase 2a's `connect`), and every role list
 * in the SPA renders `ROLE_LABELS` — so Settings → Members, its pending invitations table,
 * and the workspace switcher would print
 *
 *     Advisor, Shared: Portfolio, Shared: Documents, Shared: Entities
 *
 * against a connected firm, on screens the flag does not cover, to a family that never
 * opened the Advisors tab. Found in phase 2a's code review, when the MISSING "Shared:
 * Contacts" was a false claim (nothing was armed). Phase 2b armed the capabilities, so that
 * line is now accurate — but the rule stands unchanged for a plainer reason: the flag is the
 * one switch that decides whether this family sees the sharing vocabulary at all, and half a
 * feature leaking onto three other screens is not a coherent state to ship.
 *
 * So while the flag is off, the pillar roles are hidden everywhere and an advisor's row
 * reads "Advisor", as it did before phase 2a. With the flag on they render.
 *
 * ⚠️ **LABELS ONLY. Never route an authorization or role decision through this.** appcore
 * holds the roles either way, and every predicate over a membership — `getPrimaryRoleId`,
 * `isReadOnlyRoleSet`/`useCanWrite`, `isWorkspaceAdmin`/`RequireAdmin`, and `Access.tsx`'s
 * own `isAdvisor` check — must keep seeing the FULL list. Filtering one of those would flip
 * an advisor's membership to "writer" in the SPA, which is steward-app#643's failure mode.
 *
 * ⚠️ **Two other places render `ROLE_LABELS` and are deliberately NOT filtered through
 * this**: the ACL principal lists in `src/pages/Credentials.tsx` and
 * `src/components/folders/FolderAclManagerModal.tsx`. Both state grants that EXIST rather
 * than making a claim about what a firm can see, so hiding an entry there would be worse
 * than printing an unexpected role name. Each carries its own note; do not unify them.
 */
export function visibleRoleIds(roleIds: readonly string[]): string[] {
  if (sharingAreasEnabled()) return [...roleIds];
  // The cash flow FIRM roles are composed onto an advisor membership like a pillar role but
  // are not in `PILLAR_ROLE_IDS` (steward-app#2581), so they are added here by hand.
  const hidden = new Set<string>([...PILLAR_ROLE_IDS, ...CASH_FLOW_FIRM_ROLE_IDS]);
  return roleIds.filter(id => !hidden.has(id));
}

/**
 * appcore platform seeded admin roles. A workspace created through the platform
 * "add workspace" / founder-bootstrap flow grants its creator the platform
 * WORKSPACE_ADMIN (and, for bootstrap, TENANT_ADMIN) role — NOT the steward-app custom
 * `ADMIN` role, which is only assigned by steward-app's own seed/invite path. Owner-
 * gated UI must recognize both families, or a workspace's own creator would see no
 * management controls. appcore remains the security boundary (ws:manage for rename,
 * tenant-admin for delete); this is UX gating only.
 */
export const PLATFORM_ADMIN_ROLES = PLATFORM_ADMIN_ROLE_IDS;

/**
 * True when the membership carries any admin-capable role (steward-app ADMIN or a
 * platform tenant/workspace-admin role).
 *
 * Re-exported from `@steward-app/steward-types` rather than defined here. It moved when
 * the façade needed the same union as a real gate (`PUT /net-worth/baseline`, which hides
 * net-worth history for every member of the workspace), and this file is SPA-only. Two
 * hand-maintained copies of a role list have no mechanism to disagree loudly — the read-
 * only list in that same module shipped one role short for exactly that reason.
 */
export function isWorkspaceAdmin(roleIds: string[]): boolean {
  return isAdminRoleSet(roleIds);
}

/**
 * Roles that carry no write capability anywhere in the workspace: steward-app's own
 * `steward-viewer` (`scripts/seed-steward-app.ts` — `ws:read`, `mem:read`, `inst:read`,
 * `type:read`) and appcore's platform `workspace-viewer` (`packages/core/src/entities/role.ts`
 * — `*:read` only). Both are read-only by construction, so appcore 403s every write a
 * holder attempts; the SPA gating below only keeps us from *offering* those writes.
 */
export const PLATFORM_READ_ONLY_ROLES = PLATFORM_READ_ONLY_ROLE_IDS;

/*
 * A `READ_ONLY_ROLES` re-export of the shared list stood here until steward-app#1102 and
 * had zero consumers repo-wide. Deleted rather than renamed: the shared constant is now
 * `NO_WRITE_ROLE_IDS`, and a SPA-local alias for it would be a second name for a list whose
 * whole problem was being read under the wrong one. Import it from
 * `@steward-app/steward-types` if a consumer ever appears.
 */

/**
 * True when *every* role on the membership is a known role that confers no `inst:write`,
 * so write affordances should not be offered.
 *
 * ⚠️ NOT "the user cannot write anything in this workspace" — that sentence stood here until
 * steward-app#1102 and is false: the shared list admits `FIRM_AUTHORITY`, whose
 * `engagement:activate` puts a household onto an advisory firm's roster. The predicate is the
 * narrow one, and it is about which affordances this SPA offers, nothing more.
 *
 * Deliberately **fails open**: an unrecognized role id (a future platform role, a custom
 * workspace role) makes this false and the UI keeps showing write controls. Permissions
 * union across roles (data-model doc §4), so any unknown role could well carry writes —
 * and hiding controls from a legitimate writer is a real breakage, whereas showing them
 * to someone who can't write is exactly today's behavior (appcore returns 403). An empty
 * role list is likewise anomalous rather than proof of read-only, so it fails open too.
 *
 * This is UX gating, never the security boundary — appcore enforces capabilities on
 * every write regardless of what the SPA renders.
 */
export function isReadOnlyMember(roleIds: string[]): boolean {
  // Delegates to the shared rule so the SPA and the façade cannot drift. The comment
  // above ("never the security boundary") still describes THIS caller, but the same list
  // is now load-bearing in the façade, where a miss means a read-only member can spend
  // money — see @steward-app/steward-types roles.ts and steward-app#311.
  return isReadOnlyRoleSet(roleIds);
}

/** True when the membership carries the org (tenant) admin role — the capability that
 *  can add a workspace to the *existing* org (WC2 / decisions log §41). Narrower than
 *  {@link isWorkspaceAdmin}, which also matches workspace-scoped admins (a workspace
 *  admin who isn't a tenant-admin can't add org workspaces — appcore 403s them). */
export function isOrgAdmin(roleIds: string[]): boolean {
  return roleIds.includes(PLATFORM_ADMIN_ROLES.TENANT_ADMIN);
}

/** Workspace-quota shape from the façade `GET /workspaces/quota` (WC2). `limit`/`remaining`
 *  are null when the plan is unlimited (no cap to display). */
export interface WorkspaceQuota {
  plan: string;
  limit: number | null;
  used: number;
  remaining: number | null;
}

export const ROLE_OPTIONS = [
  { id: STEWARD_ROLES.ADMIN,  label: 'Admin'  },
  { id: STEWARD_ROLES.MEMBER, label: 'Member' },
  { id: STEWARD_ROLES.VIEWER, label: 'Viewer' },
];

// ── MFA factors + step-up (Stream 4 Phase 1, appcore #128/#130) ──
// Workspace-less, identity-scoped. The façade passes appcore's response bodies
// through verbatim (still wrapped in `data`), so these mirror appcore's own
// contract rather than being reshaped into a steward-app domain shape.

export type FactorType = 'webauthn' | 'totp';

/** One enrolled MFA factor, as returned by `GET /me/factors`. `status` is
 *  absent for a webauthn factor (active on creation) and `'pending' | 'active'`
 *  for a TOTP factor mid-activation. */
export interface Factor {
  factorId: string;
  factorType: FactorType;
  label?: string;
  status?: string;
  createdAt: string;
}

/** True when a factor counts toward "the user has a usable second factor" —
 *  absent status (webauthn) or an explicit `active` (TOTP post-activation). A
 *  TOTP factor still `pending` activation does not count. */
export function isUsableFactor(f: Factor): boolean {
  return !f.status || f.status === 'active';
}

/** `POST /me/factors/totp/enroll` response — the secret/otpauth URI are
 *  returned ONCE by appcore; never persist or log them past the enrollment
 *  screen that reads this response. */
export interface TotpEnrollment {
  factorId: string;
  otpauthUri: string;
  secret: string;
}

/** The step-up gate contract (appcore docs/28 §9): any gated call 403s this
 *  shape instead of returning data. `tier` distinguishes a lighter `view` gate
 *  from a stricter `reveal` gate (e.g. credential reveal) — a `reveal` prompt
 *  can appear again even right after a `view` one just succeeded. */
export type StepUpTier = 'view' | 'reveal';

/** What the person is doing when the ceremony opens, for the dialog's wording only. The gate and
 *  the tier are the server's; this changes no mechanics. Absent reads as `'reveal'`. */
export type StepUpAction = 'reveal' | 'save' | 'remove';

/**
 * WHICH KIND of step-up gate refused, from the 403 body (appcore docs/28 §9).
 *
 *   'floor'     — the workspace's assurance floor. EVERY workspace-scoped call will refuse, so
 *                 the shell cannot populate at all and the prompt must not be dismissible.
 *   'operation' — one deliberate gate on one action (reveal, factor management, onboarding
 *                 bootstrap). The user still holds their page, so cancelling is correct.
 *
 * Absent on an older backend, and `'operation'` is the safe default: it assumes the user still
 * holds something, which at worst offers a dismiss that does nothing useful, rather than
 * trapping someone behind a wall they cannot pass.
 */
export type StepUpReason = 'floor' | 'operation';

/**
 * Why a `409 CONFLICT` was refused, as `ConflictError` in the façade's `middleware.ts`
 * carries it (steward-app#810).
 *
 * ⚠️ **It shares the `reason` FIELD with {@link StepUpReason} and shares nothing else.**
 * `errorToResponse` puts both vocabularies in the same key, and `ApiError` carries whichever
 * arrived, so a consumer has to read `status` before it reads `reason`. Typed rather than
 * left as a bare string because the folder-visibility dialog branches on it: only the three
 * "this folder has its own access list" reasons are answered by opening the access editor,
 * and sending `too-many-principals` or `acl-changed` there would offer a tool that cannot
 * help under a heading that is not true.
 *
 * Open rather than closed (`(string & {})`): every façade handler is free to throw a
 * `ConflictError` with a reason of its own, and a client that type-errors on an unfamiliar
 * one would be asserting a contract the server never promised.
 */
export type ConflictReason =
  | 'foreign-entries' | 'advisor-scope-principal' | 'seed-role-removed'
  | 'too-many-principals' | 'acl-changed'
  | (string & {});

/**
 * The `reason` on a `429 INVITE_DAILY_LIMIT` (steward-app#2051): which of the three daily caps
 * tripped. The third vocabulary the façade puts in the shared `reason` key, after
 * {@link StepUpReason} (403) and {@link ConflictReason} (409). Open for the same reason
 * `ConflictReason` is: a newer façade may send a value this build does not know, and the SPA
 * falls back to a sentence that is true of every cap.
 */
export type RateLimitReason = 'RECIPIENT_ADDRESS' | 'FIRM_CEILING' | 'SENDER_BUDGET' | (string & {});

// ── Social login (Wave 1, appcore social-login-w1-contract.md) ──

/** How a login method came to exist on the account: the original signup
 *  (email/password bootstrap), a Google-first `/me/provision` signup, or a
 *  later `/me/logins` link ceremony. */
export type LinkMethod = 'bootstrap' | 'provisioned' | 'linked';

/** One entry in `GET /me/logins` — a way the caller can sign in. `label` is
 *  proof-derived display text (e.g. `Google (jane@x.com)` or `Email and
 *  password`); `current` marks the method behind the session making the
 *  request right now (drives the "Signed in now" chip). */
export interface Login {
  linkId: string;
  label: string;
  linkMethod: LinkMethod;
  linkedAt: string;
  current: boolean;
}

// ── Advisor-firm channel (docs/25-advisor-firm-channel.md §1/§2, S6) ──
// Client-side of the façade's `services/steward-app-api/src/handlers/advisor.ts`
// (S5, steward-app#610/#627) — see that file's header for the full contract these
// mirror. People → Advisors (src/pages/people/Advisors.tsx) is the only consumer.

/**
 * One advisor a firm OFFERS families, as `GET /advisor/firms` projects them
 * (steward-app#1343 step 7's `offeredAdvisors`).
 *
 * 🚨 **There is no `email` here and there must never be one.** That projection is a
 * hand-written PICK LIST and the outermost of three between an advisor's mailbox and a
 * browser; the façade test that guards it reads the serialized response body. Adding a field
 * to this interface would not by itself ship one, but it is how somebody starts.
 *
 * `advisorId` is an opaque, stable ULID: the connect target and the `?advisor=` deep-link
 * key. Match it with `===` against a roster this SPA fetched; never parse it, never build one.
 */
export interface AdvisorFirmOffer {
  advisorId: string;
  name: string;
  /** The firm's published title for this person ("Wealth Advisor"). Optional on the roster
   *  entry itself, so absent means the firm published none, not that the read failed. */
  title?: string;
}

/** One entry in `GET /advisor/firms`' `items` — the compile-time identity subset the
 *  façade re-serves (never the per-env tenant/workspace ids, which stay façade-only). */
export interface AdvisorFirmSummary {
  firmId: string;
  name: string;
  brandKey: string;
  /**
   * The people at this firm a family may connect to (steward-app#1343 step 8a).
   *
   * ⚠️ **ABSENT means this firm offers nobody a family can invite, and since
   * steward-app#1384 that means the finder offers nothing for it.** A connect must name one
   * person (`advisorId`), and the façade refuses one that names nobody with a 400, so the
   * rosterless firm row that used to "connect to the firm" is gone. The façade omits the key
   * entirely for a firm with no roster, and equally for one whose roster lists nobody it can
   * offer, so absent and `[]` are one fact to this SPA. Which firms publish a roster is a
   * point-in-time reading of the directory, not a property of this type: re-read it at source
   * rather than trusting a figure written here.
   *
   * ⚠️ It arrives through a bare `JSON.parse(...) as T`, so this type is an assertion and not
   * a fact: coerce the array AND its entries before rendering. `buildAdvisorOffers`
   * (`src/pages/people/advisorOffers.ts`) is the one place that does it.
   */
  advisors?: AdvisorFirmOffer[];
}

/**
 * `GET /advisor/firms` — which advisory firms this environment configures. `advisorFor`
 * is UX gating for the advisor PORTAL (src/advisor/, S7) and unused by this client-side
 * settings page, which only ever connects/disconnects a firm to the caller's own
 * workspace; kept here so the shape matches the façade response exactly.
 */
export interface AdvisorFirmsResponse {
  items: AdvisorFirmSummary[];
  advisorFor: (AdvisorFirmSummary & { firmWorkspaceId: string }) | null;
}

/**
 * `GET /advisor/offers/{advisorId}` — which firm and which person ONE connect link names
 * (steward-app#1364).
 *
 * ⚠⚠ **THIS IS THE ONLY MAPPING FROM AN `advisorId` TO A FIRM, AND IT IS DELIBERATELY NOT
 * `items`.** `GET /advisor/firms`' `items` is the family-facing OFFER directory and drops
 * `internal` firms (steward-app#1226), while an advisor's `profile.listed` — the flag that
 * decides whether their portal renders a connect link at all — has no `internal` branch by
 * design. Resolving a link against `items` therefore left a demo firm's advisor holding a live
 * link this SPA could not place at any firm. The resolution moved server-side; there is no
 * client-side id-to-firm lookup any more, and reintroducing one would reintroduce the drift.
 *
 * ⚠ **`offer: null` is the ORDINARY answer, not a failure.** An unknown id, an advisor the
 * firm has since unlisted, a firm this environment does not configure, an id two firms both
 * publish, and a link a mail client truncated all arrive here as `null`, and the page they land
 * on is the ordinary one. The route answers 200 for every one of them precisely so that none of
 * them can render as an error.
 *
 * ⚠ It arrives through a bare `JSON.parse(...) as T`, so this type is an assertion and not a
 * fact — the same rule {@link AdvisorFirmSummary.advisors} states. `linkedAdvisorOffer`
 * (`src/pages/people/advisorOffers.ts`) is the one place that coerces it.
 */
export interface AdvisorLinkedOfferResponse {
  offer: {
    firmId: string;
    firmName: string;
    advisorId: string;
    name: string;
    title?: string;
  } | null;
}

/**
 * The `advisor-connection` record's attributes (mirrors the façade's
 * `AdvisorConnectionAttributes`, `services/steward-app-api/src/types.ts`).
 *
 * ⚠️ A LABEL, never an auth artifact — see the façade handler's header. Nothing here
 * grants access; it only describes a viewer-role invite this workspace's admin started.
 */
export interface AdvisorConnectionAttributes {
  firmId: string;
  firmName: string;
  status: 'active' | 'revoked';
  advisorEmails: string[];
  connectedAt: string;
  connectedBy?: string;
  revokedAt?: string;
  /**
   * The `document-folder` holding this connection's "Shared with «firm»" folder, inside
   * the family's OWN Documents pillar (steward-app#731). Absent on connections made
   * before the field existed, and on the ones whose folder creation degraded; the next
   * connect backfills it, so treat "missing" as "not yet", never as an error.
   *
   * ⚠️ Not a permission and not a scope. The folder has no ACL entries, so it restricts
   * nothing: the advisor already reads the whole vault, and anything left at the root
   * stays just as visible. It is a designated place, not a boundary. Copy that implies
   * otherwise would be untrue (see steward-app#733).
   */
  sharedFolderId?: string;
  /**
   * The family's contributor opt-in for this connection (steward-app#742 step 4): may this
   * firm add and update documents inside the folder above?
   *
   * ⚠️ A REQUEST, never the grant. What a firm can actually do is decided by their ACL entry
   * on that folder and the roles on their membership, both written server-side by the
   * façade's reconcile. Absent on every connection made before the field existed, and that
   * reads as "no", which is the right default for a family who was never asked.
   */
  contribute?: boolean;
}

/**
 * What one of a firm's advisor mailboxes actually has in this workspace (steward-app#1310,
 * #1273; design of record #1343 Phase A). Mirrors the façade's `AdvisorPersonState`.
 *
 *   - `active`  — an active membership at that address. The only one that means access.
 *   - `invited` — a pending invitation carrying this firm's advisor role. Nobody has access
 *                 yet, and it says nothing about whether the mail arrived.
 *   - `none`    — neither. Usually a person the firm listed AFTER this family connected, who
 *                 was therefore never invited (#1310).
 */
export type AdvisorPersonState = 'active' | 'invited' | 'none';

/** One row of {@link AdvisorConnection.advisors}. `email` is lowercased. */
export interface AdvisorConnectionPerson {
  email: string;
  state: AdvisorPersonState;
  /**
   * The roster's own id for this person, when their firm publishes one
   * (steward-app#1365/#1366). Mirrors the façade's field of the same name.
   *
   * ⚠️ **A KEY, not something to render.** It is what lets this page tell a connected person
   * from an offer for the same person: an offer (`GET /advisor/firms`) carries
   * `{ advisorId, name, title }` and no address, this row carries an address and no name of its
   * own, and `advisorId` is the only field they share. `connectedAdvisorIds` in
   * `advisorOffers.ts` is the one place that reads it for the finder, and `handleResend` is the
   * one place that sends it back.
   *
   * ⚠️ **OPTIONAL, and absent is the ordinary case.** A firm with no roster has no id, an older
   * façade sends none at all (the SPA and the API are separate artifacts, steward-app#319), and
   * an address on the connection that the roster does not name has none either. Every consumer
   * must have a branch for that. Since steward-app#1384 that branch offers no per-person
   * control for the row (there is no id to send back), and the firm-wide resend that used to
   * cover it is gone.
   *
   * ⚠️ **Present does NOT mean offered.** Since steward-app#1363 the façade also serves the id,
   * with no name, for an UNLISTED roster person who holds an active membership or a live
   * invitation here (`holderRosterId`), so the family can end
   * them on their own. Such an id 404s on a connect or a resend; `addressableAdvisorId` in
   * `Advisors.tsx` is what gates Resend, and it asks the offer list, not this field.
   */
  advisorId?: string;
  /**
   * The person's published name, when their firm's roster carries one for this address
   * (steward-app#1343 step 8a).
   *
   * ⚠️ **OPTIONAL, and the row must render without it.** No firm in any environment published
   * a roster on 2026-09-10, an older façade sends no names at all (the SPA and the API are
   * separate artifacts, steward-app#319), and a firm can name some of its people and not
   * others. A missing name degrades to the ADDRESS, which is what this page showed before
   * this field existed — never to a blank and never to a dropped row.
   */
  name?: string;
  /** The person's published title. Drops independently of {@link name}: a firm can publish
   *  who somebody is without publishing what they do. */
  title?: string;
}

/**
 * One item of `GET /advisor/connections`: the stored record plus the two DERIVED fields
 * the page needs to say who can actually see this family's finances.
 *
 * ⚠️ **Both are OPTIONAL and both really do go missing.** The façade omits them when the
 * appcore reads behind them are refused (all three need `mem:manage`, which a family member
 * on the Documents page does not hold), and an older façade sends neither at all — the SPA
 * and the API are separate artifacts on separate deploys (steward-app#319). Absent means
 * "we could not read it", never "there is none", so every render path must have a branch
 * that shows what the page showed before these fields existed. Never render an `undefined`
 * as a state.
 */
export interface AdvisorConnection extends Resource<AdvisorConnectionAttributes> {
  advisors?: AdvisorConnectionPerson[];
  /**
   * `true` when the façade read its directory and this record's firm was not in it
   * (steward-app#1468). The `advisors` rows are then built from the stored snapshot and from
   * invitation provenance, and may omit somebody who holds access, so "no row is active" does
   * not mean nobody accepted. A row's `advisorId` on such a connection is a derived id that
   * only the per-person End route understands. Absent on a resolved firm and from an older
   * façade.
   */
  firmUnresolved?: boolean;
  /** The firm-side engagement pair's state. `pending` means nobody at the firm has accepted
   *  yet, so the family's household is not on the firm's roster. It grants nothing. */
  engagementStatus?: 'pending' | 'active' | 'ended';
  /**
   * On a REVOKED record only: when the FIRM ended this connection ("Remove client",
   * crossmarc/steward-app#1865), as the pair's own `endedAt`. Absent when the family ended it
   * themselves, when the family has connected to that firm again, and after 30 days. Mirrors
   * the façade's field of the same name; the façade decides it (`firmEndedNoticeAt`).
   */
  endedByFirmAt?: string;
  /**
   * The engagement pair's kind (steward-app#1507, SCS 3.4b): `'sponsored'` when this firm set
   * up the workspace and the family is under the sharing floor, `'connected'` for an ordinary
   * bring-your-own connection. Mirrors the façade's field of the same name.
   *
   * ⚠️ **Same absence rules as {@link engagementStatus}, and an absent value renders exactly
   * as it always has: an ordinary connected firm.** Never used to grant or withhold anything on
   * its own — it only selects which sentence `Advisors.tsx` shows; the server-side lock
   * (`SPONSORSHIP_LOCKED`) is what actually refuses a write.
   */
  kind?: 'connected' | 'sponsored';
}

/**
 * `DELETE /advisor/connections/{firmId}/advisors/{advisorId}` response: ending ONE person's
 * access (steward-app#1363).
 *
 * `ended` says what actually ended, and the page's notice reads it rather than guessing:
 *   - `advisor`: only that person's invitation and membership went. The connection stays,
 *     because somebody else at the firm still has access or a pending invitation here;
 *   - `connection`: they were the last person with access, so the façade ran the whole
 *     firm teardown (`DELETE /advisor/connections/{firmId}`'s own code path) and the
 *     connection has ended.
 *
 * The counts are what the request actually did: a membership DEMOTED in place (the person
 * administers this workspace) is not counted as removed.
 */
export interface AdvisorPersonEndResponse {
  ended: 'advisor' | 'connection';
  membersRemoved: number;
  invitesRevoked: number;
}

/** `POST /advisor/connections` response — the record plus what happened to each of the
 *  firm's mailboxes: invited just now, held by the F32 throttle, or skipped because that
 *  advisor already holds access in this workspace (steward-app#630). The three add up to
 *  the firm's mailbox count. */
export interface AdvisorConnectResponse extends Resource<AdvisorConnectionAttributes> {
  invitesIssued: number;
  invitesThrottled: number;
  invitesSkipped: number;
  /**
   * Of `invitesIssued`, how many invitations had their email attempted and FAIL
   * (steward-app#2001). Omitted when none did. A subset of `invitesIssued`, not a fourth part
   * of the three-way sum: the invitation exists, only the mail did not go out. A send that was
   * never attempted (mailer unconfigured) is not a failure and is not counted. Optional because
   * the SPA and the façade deploy separately (steward-app#319); read it as `> 0`.
   */
  invitesEmailFailed?: number;
  /**
   * Existing memberships the connect pinned the advisor role onto, rather than inviting
   * (steward-app#1001): the firm's mailbox already held an ordinary membership here, so
   * there was nothing to invite and the role set was corrected instead.
   *
   * NOT part of the three-way sum above — those partition the firm's mailboxes by what
   * happened to their invitation, and a pinned mailbox was skipped from invitation too.
   * Zero on every healthy connect.
   *
   * Optional because the SPA and the façade API are separate artifacts on separate
   * deploys: an SPA newer than the API must read a response that does not carry it.
   */
  advisorRolesPinned?: number;
  /**
   * **The firm still has to ACCEPT this connection** (steward-app#1146). `true` when this
   * connect wrote the engagement pair and issued no invitation at all, so there is no
   * acceptance to activate the pair: somebody at the firm has to activate it from their own
   * portal before the family appears on the firm's side.
   *
   * ⚠️ **Never re-derive this from the invite counts.** `invitesIssued: 0` with
   * `invitesSkipped: N` describes two different worlds and the counts cannot tell them
   * apart: the pair may have been written `pending` (this flag, and nobody will accept it),
   * or it may have been ALREADY ACTIVE, which is an ordinary re-POST over a live
   * relationship. Only the server knows which happened, and saying "the firm still has to
   * accept" to a family whose advisor is already reading their accounts would be false.
   *
   * ⚠️ Optional because the SPA and the façade API are separate artifacts on separate
   * deploys (steward-app#319): an SPA newer than the API must read a response that does not
   * carry it. Read it as `=== true`, so `undefined` lands on the pre-#1146 copy rather than
   * on a claim nobody made.
   */
  engagementPending?: boolean;
}

/**
 * `GET /advisor/shared-areas` — one row per ACTIVE connection (steward-app#739 phase 2a).
 *
 * ⚠️ `areas` carries AREA IDS (the `id` of a `SHARING_PILLARS` entry: `portfolio`,
 * `documents`, `contacts`, `entities`, and one per pillar added since), never role ids, and
 * the same is true of the PUT that sets them. The server maps areas to roles and refuses a
 * request that names a role id; that asymmetry is invariant 1 of the façade's
 * `handlers/advisor.ts` and there is nothing this client could add that the server honours.
 *
 * ⚠️ The PUT accepts only the LIVE areas — `LIVE_SHARING_PILLARS`, which excludes a pillar
 * granted but not yet armed — while a GET reports whatever the membership actually carries.
 * So the two sets are not guaranteed identical, and a client must not assume a value it
 * READ back is one it may WRITE. Nothing composes an in-flight pillar role today, so the
 * case is latent; the façade's `parseAreas` and `areasOfRoleSet` carry the reasoning.
 *
 * `advisorCount` is how the UI tells "shares nothing" from "nobody at the firm has accepted
 * yet". Both are an empty `areas`, and they must never read as the same sentence.
 */
export interface AdvisorSharedAreasRow {
  firmId: string;
  areas: string[];
  advisorCount: number;
  /**
   * One of this firm's advisors here ALSO holds an admin-capable role in this workspace
   * (crossmarc/steward-app#1087). What that person can see is decided by their admin role
   * and not by `areas`, so the sharing screens say so in one plain sentence.
   *
   * ⚠️ **OPTIONAL, and it must stay optional.** The SPA and the façade are separate
   * artifacts on separate deploys, so a bundle carrying this field can be served against an
   * API that does not send it yet. `undefined` renders as "no such person", which is the
   * same thing `false` renders as and the right way round: the sentence appears once the
   * server can actually say it is true, rather than a caveat drawn over a guess.
   */
  adminHeld?: boolean;
  /**
   * The wider version of the same fact: one of this firm's advisors here holds a role the
   * FAMILY granted in their own right (Admin, Member, Viewer, or a platform equivalent).
   * `adminHeld` implies this, so a renderer picks the more specific sentence first.
   *
   * ⚠️ It is what keeps the claim honest after the Members role picker is used on such a
   * membership: `steward-viewer` carries `credential:read` and `steward-member` carries
   * `credential:reveal`, so "cannot see anything in your credential vault" is false for that
   * person whichever ordinary role they end up on. Optional for the same deploy-skew reason
   * as `adminHeld`.
   */
  familyRoleHeld?: boolean;
  /**
   * **The family's standing answer to "may this firm add documents to the folder we share
   * with them?"** (steward-app#742 step 4) — the stored choice, which is what the switch on
   * the sharing screen shows.
   *
   * ⚠️ **Distinct from {@link contributing}, and the switch must read THIS one.** A family
   * can opt in before anybody at the firm has accepted their invitation, and at that moment
   * there is no membership to carry the role: rendering the derived fact would flip the
   * family's switch back off in front of them.
   *
   * Optional for the same deploy-skew reason as `adminHeld`: `undefined` reads as "not
   * opted in", which is the correct pre-#742 answer.
   */
  contribute?: boolean;
  /**
   * **What is actually granted right now:** at least one of this firm's advisors holds the
   * contributor role. The honest subject of any sentence about what the firm can do today,
   * and it goes false first when a grant has been torn down.
   *
   * It lags {@link contribute} by one admin page load: the reconcile that applies a stored
   * choice needs `mem:manage`, so a family member's own visit stores nothing and grants
   * nothing. Optional, same reason.
   */
  contributing?: boolean;
  /**
   * **The areas an active sponsorship keeps ON** (steward-app#1507, SCS 3.4b; narrowed by
   * steward-app#1971 step S1, 2026-10-01): the areas this firm's sponsorship floors AND one of
   * its covered advisors holds today, so the family cannot turn them off. Mirrors the façade's
   * field of the same name.
   *
   * ⚠️ **Absent means "no sponsorship is shown for this firm". Present with an EMPTY `locked` is
   * a different fact:** the firm sponsors, and none of its floored areas is shared right now.
   * Since S1 the façade sends `locked` as a subset of this row's `areas`. A façade that predates
   * S1 sent the whole recorded floor, shared or not, which is why `WhatYouShare.tsx` intersects
   * it with `areas` itself rather than trusting the subset.
   *
   * ⚠️ **A DISPLAY of the lock, never the lock itself.** `WhatYouShare.tsx` renders these areas
   * checked and disabled so a family sees the limit before they try to turn one off, but the
   * server's `SPONSORSHIP_LOCKED` 409 is what actually refuses the write — including against a
   * stale bundle whose row predates this field.
   *
   * `wouldLock` (steward-app#1971 S3 slice 1, 2026-10-02): the areas that are OFF and that the
   * façade says would become locked if the family turned them on. Disjoint from `areas`.
   * Optional: a façade that predates it sends none, and the screen then shows no such note.
   * Display only, like `locked`.
   */
  sponsor?: { locked: SharingPillarId[]; wouldLock?: SharingPillarId[] };
  /**
   * **What this firm sees of the family's cash flow** (docs/39 §8.5 "Firm modes",
   * steward-app#2589): `nothing`, `totals` (totals by category) or `detail` (every transaction,
   * which includes the totals). The union over the firm's advisors, like `areas`. Never part of a
   * sponsorship's `locked`: no floor includes cash flow.
   *
   * Optional for the deploy-skew reason `adminHeld` gives: a façade that predates it sends none,
   * and `WhatYouShare.tsx` then renders no cash flow control at all (that façade would 400 a
   * `cashFlow` field on PUT). On PUT the field is optional too, and absent means "leave it as it
   * is", so the area and contributor switches never send it.
   */
  cashFlow?: FirmCashFlowMode;
}

export interface AdvisorSharedAreasResponse {
  items: AdvisorSharedAreasRow[];
  /**
   * Present only when the request carried `?connectFirm=` and the firm resolves
   * (steward-app#1969). `true`: the firm already has an active advisor at a registry address, so
   * a connect composes that firm's existing areas. `false`: it composes the default. Absent means
   * UNKNOWN, never `false`; the connect dialog renders no disclosure for it. Computed by the
   * same function `connect` pins roles with, and independent of any connection record.
   */
  connectReusesAreas?: boolean;
}

/** `PUT /advisor/shared-areas/{firmId}` — the areas actually in force after the write. */
export interface AdvisorSharedAreasUpdate extends AdvisorSharedAreasRow {
  membersUpdated: number;
}

export const DOCUMENT_CATEGORIES = ['Tax', 'Legal', 'Financial', 'Investment', 'Insurance', 'Estate', 'Other'];
export const ACCOUNT_TYPES = ['Brokerage', 'Retirement', 'Trust', 'Checking', 'Savings', 'Credit Card', 'Loan', 'Mortgage', 'Line of Credit', 'Other'];
// Asset classes are NOT a flat list of display strings any more: the class registry in
// `@steward-app/steward-types` holds a stable stored code, a display label, synonyms,
// the reporting group, and the per-class field order. Import from `src/lib/assetClass`.
export const ENTITY_TYPES = ['LLC', 'LP', 'Trust', 'S-Corp', 'C-Corp', 'Individual', 'Other'];
export const ENTITY_STATUSES = ['Active', 'Dissolved', 'Pending'];
export const LIABILITY_TYPES = ['mortgage', 'loan', 'line-of-credit', 'heloc', 'other'] as const;
export const LOT_TYPES = ['purchase', 'sale', 'adjustment'] as const;

/** One announcement as `GET /announcements` returns it (appcore `toAnnouncementMemberView`). */
export interface Announcement {
  id: string;
  title: string;
  /** Markdown. May hold the `{product}` token. */
  body: string;
  audience: Array<'families' | 'firms'>;
  popup: boolean;
  popupUntil: string | null;
  /** An in-app path, validated again with `inAppPath` before use. */
  link: string | null;
  /** The date a person reads (`YYYY-MM-DD`). */
  displayDate: string;
  /** When it went live (ISO). Drives the badge. */
  publishedAt: string;
  hideOnBrandedHosts: boolean;
}

export interface AnnouncementsResponse {
  /** The audience appcore derived for the active workspace. Use it, not a client-side guess. */
  viewerAudience: 'families' | 'firms';
  items: Announcement[];
}
